import React, { useState, useEffect, useCallback } from "react";
import MonthlyEmailPanel from "../components/MonthlyEmailPanel";
import { getAuditData, getWaveformTechSummary } from "../lib/api";
import { FilterDropdown, Checkbox } from "../components/SharedComponents";
import { techDisplayName } from "../lib/names";

// ── Report type definitions ───────────────────────────────────────────────────
const REPORT_TYPES = [
  {
    id: "internal", label: "Group QA Report", limitation: null,
    filters: ["team", "technologist"],
    description: "Scores by technologist and month across a team or the whole company",
    color: "#2563eb", bgColor: "#eff6ff", borderColor: "#bfdbfe",
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>,
  },
  {
    id: "individual",
    label: "Individual Tech QA Report",
    requiresTech: true,
    limitation: null,
    filters: ["team"],
    description: "Per-technologist performance review with company comparison and coaching focus areas",
    color: "#0369a1", bgColor: "#f0f9ff", borderColor: "#bae6fd",
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>,
  },
  // {
  //   id: "accreditation", label: "Accreditation Report", subLabel: "ABRET / ASNM", limitation: null,
  //   description: "Formal QA documentation for accreditation bodies and CNIM technologist logs",
  //   color: "#0d9488", bgColor: "#f0fdfa", borderColor: "#99f6e4",
  //   icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>,
  // },
  // {
  //   id: "hospital", label: "Hospital / Surgery Center", limitation: null,
  //   filters: ["region", "facility", "procedureType"],
  //   description: "Service quality report for hospital administrators — facility, surgeon, and alert focus",
  //   color: "#7c3aed", bgColor: "#faf5ff", borderColor: "#ddd6fe",
  //   icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0H5m0 0H3m12-13h-4m2-2v4M7 21v-4a2 2 0 012-2h2a2 2 0 012 2v4"/></svg>,
  // },
  // {
  //   id: "medicare", label: "Medicare / Payer Audit",
  //   description: "Documentation compliance evidence for CPT 95940 / G0453 payer audit response",
  //   color: "#b45309", bgColor: "#fffbeb", borderColor: "#fde68a",
  //   limitation: "CPT billing codes require USMON integration. Concurrent case conflict detection requires USMON scheduling data.",
  //   icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/></svg>,
  // },
];

// The Individual report's accent colour, shared with the technologist's own
// monthly report page so both look the same.
export const INDIVIDUAL_REPORT_COLOR = REPORT_TYPES.find(r => r.id === "individual").color;

const PRESETS = [
  { id: "this_month", label: "This Month" },
  { id: "last_month", label: "Last Month" },
  { id: "this_qtr",   label: "This Quarter" },
  { id: "last_qtr",   label: "Last Quarter" },
  { id: "last_6m",    label: "Last 6 Months" },
  { id: "ytd",        label: "Year to Date" },
  { id: "all",        label: "All Time" },
];

const TIERS = [
  { id: "above",    label: "Above Average", min: 95, range: "95+",   color: "#1D9E75", bg: "#ecfdf5", border: "#a7f3d0" },
  { id: "average",  label: "Average",       min: 90, range: "90-94", color: "#185FA5", bg: "#eff6ff", border: "#bfdbfe" },
  { id: "below",    label: "Below Average", min: 80, range: "80-89", color: "#854F0B", bg: "#fffbeb", border: "#fde68a" },
  { id: "critical", label: "Critical",      min: 0,  range: "<80",   color: "#A32D2D", bg: "#fef2f2", border: "#fecaca" },
];

export function tierFor(score) {
  if (score == null) return { id: "none", label: "No data", range: "", color: "#94a3b8", bg: "#f8fafc", border: "#e2e8f0" };
  return TIERS.find(t => score >= t.min) || TIERS[TIERS.length - 1];
}

// Minimum cases before a modality tile is trusted enough to show a score.
const MIN_MODALITY_CASES = 1;

// Check IDs carrying the IONM START / STOP timestamps in their evidence field.
const IONM_START_CHECKS = ["17"];
const IONM_STOP_CHECKS  = ["35", "38"];

const findCheck       = (checks, ids) => checks.find(c => ids.includes(String(c.check_id)));
const hasPassingCheck = (d, ids) => {
  const c = findCheck(d._tech_checks || [], ids);
  return !!c && c.result === "PASS";
};

// Score one slice of a tech's checks by points. N/A is excluded from both
// sides; UNABLE TO DETERMINE is excluded and counted so it can be surfaced
// as a data gap rather than scored against the tech. A case with nothing
// scorable in the slice is left out of the case count too.
function scoreCheckGroup(docs, matches) {
  let earned = 0, possible = 0, checkCount = 0, caseCount = 0, unscorable = 0;
  docs.forEach(d => {
    const checks = (d._tech_checks || []).filter(c => matches(c.category));
    let scoredHere = 0;
    checks.forEach(c => {
      const result = String(c.result || "").toUpperCase().replace(/_/g, " ");
      if (result === "NOT APPLICABLE") return;
      if (result === "UNABLE TO DETERMINE") { unscorable++; return; }
      const pts = Number(c.max_points) > 0 ? Number(c.max_points) : 1;
      possible += pts;
      if (result === "PASS") earned += pts;
      scoredHere++;
    });
    if (!scoredHere) return;
    caseCount++;
    checkCount += scoredHere;
  });
  return { score: possible ? Math.round((earned / possible) * 100) : null, earned, possible, caseCount, checkCount, unscorable };
}

// Tech x month matrix. All checks included.
function buildTechMatrix(docs, maxMonths = 12) {
  const monthSet = new Set(), byTech = {};
  docs.forEach(d => {
    const sd = d.service_date || "";
    if (!sd) return;                       // no SQL date, can't place it in a column
    const month = sd.slice(0, 7);
    const s = scoreCheckGroup([d], () => true);
    if (!s.possible && !d.waveform_possible) return;
    monthSet.add(month);
    const name = d.technologist_name || "Unknown";
    const t = byTech[name] || (byTech[name] = { cells: {}, earned: 0, possible: 0, caseCount: 0 });
    const c = t.cells[month] || (t.cells[month] = { earned: 0, possible: 0, caseCount: 0 });
    const earned   = s.earned   + (d.waveform_earned   || 0);
    const possible = s.possible + (d.waveform_possible || 0);
    c.earned += earned; c.possible += possible; c.caseCount += 1;
    t.earned += earned; t.possible += possible; t.caseCount += 1;
  });

  const months = Array.from(monthSet).sort().slice(-maxMonths);
  const multiYear = new Set(months.map(m => m.slice(0, 4))).size > 1;
  const monthLabels = months.map(m => {
    const label = new Date(m + "-01T12:00:00").toLocaleString(undefined, { month: "short" });
    return multiYear ? `${label} '${m.slice(2, 4)}` : label;
  });

  const rows = Object.entries(byTech)
    .filter(([name, t]) => name && name !== "Unknown" && t.possible > 0)
    .map(([name, t]) => ({
      name,
      caseCount: t.caseCount,
      score: Math.round((t.earned / t.possible) * 100),
      cells: months.map(m => {
        const c = t.cells[m];
        return c && c.possible
          ? { score: Math.round((c.earned / c.possible) * 100), caseCount: c.caseCount }
          : { score: null, caseCount: 0 };
      }),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const scores = rows.map(r => r.score);
  const barFloor = scores.length ? Math.min(80, ...scores) : 0;

  const trend = months.map((m, i) => {
    let earned = 0, possible = 0, caseCount = 0;
    Object.values(byTech).forEach(t => {
      const c = t.cells[m];
      if (!c || !c.possible) return;
      earned += c.earned; possible += c.possible; caseCount += c.caseCount;
    });
    return { month: m, label: monthLabels[i], score: possible ? Math.round((earned / possible) * 100) : null, caseCount };
  });

  return { months, monthLabels, rows, barFloor, trend };
}

// Explicit quarter picker (last 8), keeps the chip row from growing
function quarterOptions() {
  const now = new Date();
  let y = now.getFullYear(), q = Math.floor(now.getMonth() / 3) + 1;
  const out = [];
  for (let i = 0; i < 8; i++) { out.push(`${y} Q${q}`); if (--q < 1) { q = 4; y -= 1; } }
  return out;
}
function quarterDates(label) {
  const m = /^(\d{4}) Q([1-4])$/.exec(label);
  if (!m) return { startDate: "", endDate: "" };
  const y = +m[1], sm = (+m[2] - 1) * 3;
  return { startDate: toDateStr(new Date(y, sm, 1)), endDate: toDateStr(new Date(y, sm + 3, 0)) };
}

// ── Date helpers ──────────────────────────────────────────────────────────────
function toDateStr(d) { return d.toISOString().split("T")[0]; }
function getPresetDates(p) {
  const now = new Date();
  switch (p) {
    case "this_month": return { startDate: toDateStr(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: toDateStr(now) };
    case "last_month": return { startDate: toDateStr(new Date(now.getFullYear(), now.getMonth() - 1, 1)), endDate: toDateStr(new Date(now.getFullYear(), now.getMonth(), 0)) };
    case "this_qtr": { const q = Math.floor(now.getMonth() / 3); return { startDate: toDateStr(new Date(now.getFullYear(), q * 3, 1)), endDate: toDateStr(new Date(now.getFullYear(), q * 3 + 3, 0)) }; }
    case "last_qtr": { const q = Math.floor(now.getMonth() / 3) - 1; const y = q < 0 ? now.getFullYear() - 1 : now.getFullYear(); const qq = (q + 4) % 4; return { startDate: toDateStr(new Date(y, qq * 3, 1)), endDate: toDateStr(new Date(y, qq * 3 + 3, 0)) }; }
    case "last_6m":    return { startDate: toDateStr(new Date(now.getTime() - 180 * 86400000)), endDate: toDateStr(now) };
    case "ytd":        return { startDate: toDateStr(new Date(now.getFullYear(), 0, 1)), endDate: toDateStr(now) };
    case "all":        return { startDate: "", endDate: "" };
    default:           return { startDate: toDateStr(new Date(now.getTime() - 30 * 86400000)), endDate: toDateStr(now) };
  }
}

// ── Normalize SQL case details onto the field names the reports already use.
// One place, so every table / CSV / dropdown / sort picks up SQL values.
//   service_date -> processed_at   (noon local, so YYYY-MM-DD never rolls back a day)
//   tech1        -> technologist_name
//   reader1      -> monitoring_neurologist
export function normalizeDocs(raw) {
  if (!raw?.documents) return raw;
  return {
    ...raw,
    documents: raw.documents.map(d => ({
      ...d,
      processed_at:           d.service_date ? d.service_date + "T12:00:00" : "",
      technologist_name:      techDisplayName(d.tech_name) || "Unknown",
      tech2_name:             techDisplayName(d.tech2_name) || "",
      monitoring_neurologist: d.reader_name || "Unknown",
      procedure_type:         (() => {
                                const p = (d.procedure_name || d.procedure_type || "").trim();
                                return !p || p.toUpperCase() === "UNKNOWN" ? "Unknown" : p;
                              })(),
    })),
  };
}

// Array-valued filters: an empty array means no filter, matching FilterDropdown's
// own "nothing selected = show everything" behaviour.
const inList = (list, val) => !Array.isArray(list) || list.length === 0 || list.includes(val);

// ── Filter documents ──────────────────────────────────────────────────────────
const UNASSIGNED_TEAM = "__unassigned__";

// Team as it reads in report titles: "AXIS - ABILENE/LUBBOCK" -> "Abilene/Lubbock".
// The company name already heads the report, so the "AXIS - " prefix is dropped.
function teamLabel(team) {
  if (!team || team === "ALL") return "";
  if (team === UNASSIGNED_TEAM) return "Unassigned";
  return team.replace(/^\s*AXIS\s*-\s*/i, "")
    .toLowerCase().replace(/(^|[\s/-])([a-z])/g, (m, p, c) => p + c.toUpperCase());
}

function filterDocs(docs, config) {
  return docs.filter(d => {
    if (config.startDate || config.endDate) {
      const sd = d.service_date;              // YYYY-MM-DD from SQL
      if (!sd) return false;
      if (config.startDate && sd < config.startDate) return false;
      if (config.endDate   && sd > config.endDate)   return false;
    }
    if (!inList(config.procedureType, d.procedure_type))    return false;
    if (!inList(config.technologist,  d.technologist_name)) return false;
    if (!inList(config.facility,      d.facility))          return false;
    if (config.region && config.region !== "ALL" && d.region !== config.region) return false;
    // Team is the tech's assignment on the Users page, not the case's region.
    if (config.team && config.team !== "ALL") {
      if (config.team === UNASSIGNED_TEAM ? !!d.team : d.team !== config.team) return false;
    }
    // Tech 1 + Tech 2 cases: Tech 1's grade would include Tech 2's work.
    if (config.excludeDualTech && d.tech2_name) return false;
    return true;
  });
}

// ── Compute all report data ───────────────────────────────────────────────────
function computeReportData(docs, reportType, config, waveformSummary) {
  if (!docs.length) return null;

  // Tag each doc with its tech-only checks so reader (Interpreting_Physician)
  // checks never leak into tech scoring.
  docs.forEach(d => { d._tech_checks = (d.qa_checks || []).filter(c => c.audit_type !== "Interpreting_Physician"); });

  // Enrich each doc FIRST — fix scores, derive timing
  docs.forEach(d => {
    // Points-based, weighted, N/A and UNABLE TO DETERMINE excluded from both sides.
    const s = scoreCheckGroup([d], () => true);
    d.qa_earned   = s.earned;
    d.qa_possible = s.possible;
    d.qa_score    = s.score == null ? 0 : s.score;

    const applicable = (d._tech_checks || []).filter(c => c.result !== "NOT_APPLICABLE");
    d.pass_count   = applicable.filter(c => c.result === "PASS").length;
    d.fail_count   = applicable.filter(c => c.result === "FAIL").length;
    d.total_checks = applicable.length;

    const checks = d._tech_checks || [];
    const startCheck = findCheck(checks, IONM_START_CHECKS);
    const stopCheck  = findCheck(checks, IONM_STOP_CHECKS);
    const _extractTime = (ev) => {
      if (!ev) return null;
      // Try HH:MM:SS or HH:MM (with optional AM/PM)
      const colonMatch = ev.match(/\d{1,2}:\d{2}(?::\d{2})?(?:\s*[APap][Mm])?/);
      if (colonMatch) return colonMatch[0];
      // Try 4-digit military time (e.g., 1341, 1700)
      const milMatch = ev.match(/\b(\d{4})\b/);
      if (milMatch) {
        const h = milMatch[1].substring(0, 2), m = milMatch[1].substring(2, 4);
        if (parseInt(h) < 24 && parseInt(m) < 60) return `${h}:${m}`;
      }
      return null;
    };
    d.ionm_start_time = startCheck?.result === "PASS" ? (_extractTime(startCheck.evidence) || "Documented") : null;
    d.ionm_stop_time  = stopCheck?.result === "PASS"  ? (_extractTime(stopCheck.evidence)  || "Documented") : null;
    if (d.ionm_start_time && d.ionm_stop_time) {
      try {
        const parseTime = (t) => {
          const m = t.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*([APap][Mm]))?/);
          if (!m) return null;
          let h = parseInt(m[1]), min = parseInt(m[2]);
          if (m[4] && m[4].toUpperCase() === "PM" && h < 12) h += 12;
          if (m[4] && m[4].toUpperCase() === "AM" && h === 12) h = 0;
          return h * 60 + min;
        };
        const s = parseTime(d.ionm_start_time), e = parseTime(d.ionm_stop_time);
        if (s != null && e != null && e > s) {
          d.monitoring_duration_mins = e - s;
          d.billing_units_95940 = Math.ceil((e - s) / 15);
        }
      } catch {}
    }
    d.documentation_gaps = [];
    if (!d.ionm_start_time) d.documentation_gaps.push("IONM START not documented");
    if (!d.ionm_stop_time)  d.documentation_gaps.push("IONM STOP not documented");
  });

  const total    = docs.length;
  const passed   = docs.filter(d => d.fail_count === 0).length;
  const failed   = total - passed;
  const passRate = total ? Math.round((passed / total) * 100) : 0;
  // Pooled across every case with scoreCheckGroup, which weights by
  // max_points. Averaging each case's percentage let a three-check case
  // count as much as a seventy-check one.
  const avgScore = scoreCheckGroup(docs, () => true).score;
  const reviewed = docs.filter(d => ["REVIEWED","CLOSED"].includes(d.reviewer_status)).length;
  const pending  = docs.filter(d => d.reviewer_status === "PENDING_REVIEW").length;
  // Review rate = % of failed cases reviewed
  const failedDocs   = docs.filter(d => d.fail_count > 0);
  const reviewRate   = failedDocs.length ? Math.round((failedDocs.filter(d => ["REVIEWED","CLOSED"].includes(d.reviewer_status)).length / failedDocs.length) * 100) : 100;

  // Top failed checks
  const chkFail = {}, chkTotal = {};
  docs.forEach(d => (d._tech_checks || []).forEach(c => {
    chkTotal[c.check_id] = (chkTotal[c.check_id] || 0) + 1;
    if (!chkFail[c.check_id]) chkFail[c.check_id] = { question: c.question, category: c.category, count: 0 };
    if (c.result === "FAIL") chkFail[c.check_id].count++;
  }));
  const topFailed = Object.entries(chkFail)
    .filter(([, v]) => v.count > 0)
    .map(([id, v]) => ({ check_id: id, question: v.question, category: v.category, fail_count: v.count, total: chkTotal[id] || 1, fail_rate: Math.round((v.count / (chkTotal[id] || 1)) * 100) }))
    .sort((a, b) => b.fail_count - a.fail_count).slice(0, 10);

  // By procedure
  const byProc = {};
  docs.forEach(d => {
    const k = d.procedure_type || "Unknown";
    if (!byProc[k]) byProc[k] = { total: 0, passed: 0, failed: 0, earned: 0, possible: 0, waveCases: 0 };
    byProc[k].total++;
    byProc[k].earned   += (d.qa_earned   || 0) + (d.waveform_earned   || 0);
    byProc[k].possible += (d.qa_possible || 0) + (d.waveform_possible || 0);
    if (d.waveform_possible > 0) byProc[k].waveCases++;
    d.fail_count === 0 ? byProc[k].passed++ : byProc[k].failed++;
  });
  Object.values(byProc).forEach(v => {
    v.avg_score = v.possible ? Math.round((v.earned / v.possible) * 100) : 0;
    v.pass_rate = v.total ? Math.round((v.passed / v.total) * 100) : 0;
  });

  // By technician
  const byTech = {};
  docs.forEach(d => {
    const k = d.technologist_name || "Unknown";
    if (!byTech[k]) byTech[k] = { total: 0, passed: 0, failed: 0, earned: 0, possible: 0, modalities: new Set() };
    byTech[k].total++;
    byTech[k].earned   += (d.qa_earned   || 0) + (d.waveform_earned   || 0);
    byTech[k].possible += (d.qa_possible || 0) + (d.waveform_possible || 0);
    d.fail_count === 0 ? byTech[k].passed++ : byTech[k].failed++;
    if (d.modalities_used) byTech[k].modalities.add(d.modalities_used);
  });
  Object.values(byTech).forEach(v => {
    v.avg_score  = v.possible ? Math.round((v.earned / v.possible) * 100) : 0;
    v.pass_rate  = v.total ? Math.round((v.passed / v.total) * 100) : 0;
    v.modalities = Array.from(v.modalities).join(", ") || "Unknown";
  });

  // By surgeon
  const bySurgeon = {};
  docs.forEach(d => {
    const k = d.surgeon_name || "Unknown";
    if (!bySurgeon[k]) bySurgeon[k] = { total: 0, passed: 0, failed: 0 };
    bySurgeon[k].total++;
    d.fail_count === 0 ? bySurgeon[k].passed++ : bySurgeon[k].failed++;
  });
  Object.values(bySurgeon).forEach(v => { v.pass_rate = v.total ? Math.round((v.passed / v.total) * 100) : 0; });

  // By facility
  const byFacility = {};
  docs.forEach(d => {
    const k = d.facility || "Unknown";
    if (!byFacility[k]) byFacility[k] = { total: 0, passed: 0, failed: 0 };
    byFacility[k].total++;
    d.fail_count === 0 ? byFacility[k].passed++ : byFacility[k].failed++;
  });
  Object.values(byFacility).forEach(v => { v.pass_rate = v.total ? Math.round((v.passed / v.total) * 100) : 0; });

  // Modalities
  const modalities = { SSEP: 0, MEP: 0, EMG: 0, EEG: 0, BAER: 0 };
  docs.forEach(d => {
    const m = (d.modalities_used || "").toUpperCase();
    if (m.includes("SSEP")) modalities.SSEP++;
    if (m.includes("MEP"))  modalities.MEP++;
    if (m.includes("EMG"))  modalities.EMG++;
    if (m.includes("EEG"))  modalities.EEG++;
    if (m.includes("BAER") || m.includes("BAEP")) modalities.BAER++;
  });

  // QA category performance
  const catPerf = {};
  docs.forEach(d => (d._tech_checks || []).forEach(c => {
    if (c.result === "NOT_APPLICABLE") return;
    if (!catPerf[c.category]) catPerf[c.category] = { pass: 0, fail: 0, total: 0 };
    catPerf[c.category].total++;
    c.result === "PASS" ? catPerf[c.category].pass++ : catPerf[c.category].fail++;
  }));
  Object.values(catPerf).forEach(v => { v.rate = v.total ? Math.round((v.pass / v.total) * 100) : 0; });

  // Documentation rates
  const startCount = docs.filter(d => hasPassingCheck(d, IONM_START_CHECKS)).length;
  const stopCount  = docs.filter(d => hasPassingCheck(d, IONM_STOP_CHECKS)).length;
  const bothCount  = docs.filter(d => hasPassingCheck(d, IONM_START_CHECKS) && hasPassingCheck(d, IONM_STOP_CHECKS)).length;
  const docRates   = {
    start_rate: total ? Math.round((startCount / total) * 100) : 0,
    stop_rate:  total ? Math.round((stopCount  / total) * 100) : 0,
    both_rate:  total ? Math.round((bothCount  / total) * 100) : 0,
    start_count: startCount, stop_count: stopCount, both_count: bothCount,
  };

  const docGapCases = docs.filter(d => !hasPassingCheck(d, IONM_START_CHECKS) || !hasPassingCheck(d, IONM_STOP_CHECKS));
  const unresolvedFailed = docs.filter(d => d.fail_count > 0 && d.reviewer_status === "PENDING_REVIEW");

  // Auto action items
  const actionItems = [];
  if (topFailed.length > 0 && topFailed[0].fail_rate > 30)
    actionItems.push(`"${topFailed[0].question.substring(0, 60)}…" fails in ${topFailed[0].fail_rate}% of cases — targeted training recommended`);
  const lowTechs = Object.entries(byTech).filter(([, v]) => v.total >= 3 && v.avg_score < 90);
  if (lowTechs.length) actionItems.push(`${lowTechs.length} technologist(s) averaging below 90% — schedule coaching sessions`);
  if (docRates.both_rate < 80) actionItems.push(`IONM START/STOP fully documented in only ${docRates.both_rate}% of cases — reinforce documentation protocol`);
  if (pending > 0) actionItems.push(`${pending} failed case(s) awaiting reviewer decision — assign for review`);
  if (!actionItems.length) actionItems.push("No immediate action items identified — maintain current quality standards");

  // Data gaps
  const gaps = [];
  const noTech    = docs.filter(d => !d.technologist_name || d.technologist_name === "Unknown").length;
  const noSurgeon = docs.filter(d => !d.surgeon_name || d.surgeon_name === "Unknown").length;
  const noProc    = docs.filter(d => !d.procedure_type || d.procedure_type === "Unknown").length;
  const noFac     = docs.filter(d => !d.facility || d.facility === "Unknown").length;
  if (noTech)    gaps.push(`${noTech} case(s) missing technologist name — reprocess documents to capture`);
  if (noSurgeon) gaps.push(`${noSurgeon} case(s) missing surgeon name — reprocess documents to capture`);
  if (noProc)    gaps.push(`${noProc} case(s) missing procedure type`);
  if (reportType === "hospital" && noFac) gaps.push(`${noFac} case(s) missing facility name — reprocess to capture`);
  if (reportType === "medicare") {
    gaps.push("CPT billing codes not available — USMON integration required");
    gaps.push("Concurrent case conflict detection requires USMON scheduling data");
  }

  // ── Headline scores ──
  const documentation = scoreCheckGroup(docs, () => true);
  // Waveform pooled from the same per-case points the procedure rows use, so
  // Overall reconciles with every breakdown on the page.
  const waveform = docs.reduce((a, d) => {
    a.earned   += d.waveform_earned   || 0;
    a.possible += d.waveform_possible || 0;
    if (d.waveform_possible > 0) a.caseCount++;
    return a;
  }, { earned: 0, possible: 0, caseCount: 0 });
  waveform.score = waveform.possible ? Math.round((waveform.earned / waveform.possible) * 100) : null;
  const mso           = { score: null, pending: true };
  const techMatrix    = buildTechMatrix(docs);

  // Combined = pooled numerator over pooled denominator, not an average of the two.
  const overallEarned   = (documentation.earned || 0) + (waveform.earned || 0);
  const overallPossible = (documentation.possible || 0) + (waveform.possible || 0);
  const overall         = overallPossible ? Math.round((overallEarned / overallPossible) * 100) : null;

  return {
    total, passed, failed, passRate, avgScore, reviewed, pending, reviewRate,
    documentation, waveform, mso, overall, techMatrix,
    topFailed, byProc, byTech, bySurgeon, byFacility,
    modalities, catPerf, docRates, docGapCases, unresolvedFailed,
    actionItems, gaps, docs, reportType, config,
    companyName: config.companyName || "",
    generatedAt: new Date().toLocaleString(),
  };
}

// ───────────────────────────────────────────────────────────────────────────
// computeIndividualData > Takes ALL docs in the date window (NOT filtered by tech), plus the selected
// tech name. Splits by tech internally so the comparison has something to
// compare against, then returns the selected tech's slice alongside company
// context. waveformSummary is the /waveform/tech-summary payload (or null).
// ───────────────────────────────────────────────────────────────────────────
// The period just before [startDate, endDate]: the previous calendar month when
// the range is exactly one month, otherwise the same number of days before.
export function previousPeriod(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const s = new Date(startDate + "T00:00:00"), e = new Date(endDate + "T00:00:00");
  const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const lastDay = new Date(e.getFullYear(), e.getMonth() + 1, 0).getDate();
  if (s.getDate() === 1 && e.getDate() === lastDay && s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    const ps = new Date(s.getFullYear(), s.getMonth() - 1, 1), pe = new Date(s.getFullYear(), s.getMonth(), 0);
    return { startDate: iso(ps), endDate: iso(pe), label: ps.toLocaleDateString(undefined, { month: "long" }), monthly: true };
  }
  const days = Math.round((e - s) / 86400000) + 1;
  const pe = new Date(s); pe.setDate(pe.getDate() - 1);
  const ps = new Date(pe); ps.setDate(ps.getDate() - days + 1);
  return { startDate: iso(ps), endDate: iso(pe), label: "the previous period", monthly: false };
}

// prevDocs: cases from the period before, any tech; only this tech's are used.
export function computeIndividualData(allDocs, techName, config, waveformSummary, prevDocs = null) {
  if (!allDocs.length || !techName) return null;

  // Reuse the existing enrichment by running the standard compute over
  // everything — it fixes qa_score, timing, gaps on every doc in place.
  const companyData = computeReportData(allDocs, "individual", config);
  if (!companyData) return null;

  const myDocs = allDocs.filter(d => d.technologist_name === techName);
  if (!myDocs.length) return { techName, noData: true, config, companyName: config.companyName || "", generatedAt: new Date().toLocaleString() };

  // ── This tech's headline numbers ──
  const total    = myDocs.length;
  const passed   = myDocs.filter(d => d.fail_count === 0).length;
  const failed   = total - passed;
  const passRate = Math.round((passed / total) * 100);

  // Pooled points, so this ties to the same tech's row in the group report.
  // Pooled points including waveform, so this matches this tech's row in the
  // group report and their position in the ranking below.
  const myEarned   = myDocs.reduce((s, d) => s + (d.qa_earned   || 0) + (d.waveform_earned   || 0), 0);
  const myPossible = myDocs.reduce((s, d) => s + (d.qa_possible || 0) + (d.waveform_possible || 0), 0);
  const avgScore   = myPossible ? Math.round((myEarned / myPossible) * 100) : 0;
  const documentation = scoreCheckGroup(myDocs, () => true);
  const waveform = myDocs.reduce((a, d) => {
    a.earned   += d.waveform_earned   || 0;
    a.possible += d.waveform_possible || 0;
    if (d.waveform_possible > 0) a.caseCount++;
    return a;
  }, { earned: 0, possible: 0, caseCount: 0 });
  waveform.score = waveform.possible ? Math.round((waveform.earned / waveform.possible) * 100) : null;

  // ── Company context: every tech's average, for comparison + rank ──
  // Uses byTech from the standard compute (already averaged per tech).
  const allTechEntries = Object.entries(companyData.byTech)
    .filter(([name, v]) => name && name !== "Unknown" && v.total > 0);
  
  // Pooled across every tech's points rather than averaging per-tech averages,
  // so a tech with three cases doesn't pull the company line as hard as one
  // with forty.
  const companyEarned   = allTechEntries.reduce((s, [, v]) => s + (v.earned   || 0), 0);
  const companyPossible = allTechEntries.reduce((s, [, v]) => s + (v.possible || 0), 0);
  const companyAvg      = companyPossible ? Math.round((companyEarned / companyPossible) * 100) : 0;

  const overallEarned   = (documentation.earned || 0) + (waveform.earned   || 0);
  const overallPossible = (documentation.possible || 0) + (waveform.possible || 0);
  const overall         = overallPossible ? Math.round((overallEarned / overallPossible) * 100) : null;

  // No rank and no peer tier counts: the client found ranking made techs
  // anxious (9/21/2026). A tech sees the company average and their own change
  // from the previous period instead.
  const prevPeriod = previousPeriod(config.startDate, config.endDate);
  let prevScore = null;
  const prevMine = (prevDocs || []).filter(d => d.technologist_name === techName);
  if (prevMine.length) {
    computeReportData(prevMine, "individual", config);   // fills qa_earned / qa_possible
    const pe = prevMine.reduce((s, d) => s + (d.qa_earned || 0) + (d.waveform_earned || 0), 0);
    const pp = prevMine.reduce((s, d) => s + (d.qa_possible || 0) + (d.waveform_possible || 0), 0);
    prevScore = pp ? Math.round((pe / pp) * 100) : null;
  }

  // ── Score by procedure type (this tech only) ──
  const byProc = {};
  myDocs.forEach(d => {
    const k = d.procedure_type || "Unknown";
    if (!byProc[k]) byProc[k] = { total: 0, earned: 0, possible: 0, passed: 0, waveCases: 0 };
    byProc[k].total++;
    byProc[k].earned   += (d.qa_earned   || 0) + (d.waveform_earned   || 0);
    byProc[k].possible += (d.qa_possible || 0) + (d.waveform_possible || 0);
    if (d.waveform_possible > 0) byProc[k].waveCases++;
    if (d.fail_count === 0) byProc[k].passed++;
  });
  const procList = Object.entries(byProc).map(([name, v]) => ({
    name,
    total: v.total,
    waveCases: v.waveCases,
    avg_score: v.possible ? Math.round((v.earned / v.possible) * 100) : 0,
    pass_rate: Math.round((v.passed / v.total) * 100),
    // Below this many cases, the average isn't meaningful enough to coach on.
    sparse: v.total < 3,
  })).sort((a, b) => b.avg_score - a.avg_score);

  // ── Bottom 5 specific checks (this tech's most-failed) ──
  const chkFail = {}, chkTotal = {};
  myDocs.forEach(d => (d._tech_checks || []).forEach(c => {
    if (c.result === "NOT_APPLICABLE") return;
    chkTotal[c.check_id] = (chkTotal[c.check_id] || 0) + 1;
    if (!chkFail[c.check_id]) chkFail[c.check_id] = { question: c.question, category: c.category, count: 0 };
    if (c.result === "FAIL") chkFail[c.check_id].count++;
  }));
  const bottomChecks = Object.entries(chkFail)
    .filter(([, v]) => v.count > 0)
    .map(([id, v]) => ({
      check_id: id, question: v.question, category: v.category,
      fail_count: v.count, total: chkTotal[id] || 1,
      fail_rate: Math.round((v.count / (chkTotal[id] || 1)) * 100),
    }))
    .sort((a, b) => b.fail_rate - a.fail_rate || b.fail_count - a.fail_count)
    .slice(0, 5);

  // ── Modality treemap data (from the waveform endpoint) ──
  // The summary is keyed by the full SQL name; techName has credentials stripped.
  const myWaveform = Object.entries(waveformSummary?.techs || {})
    .find(([k]) => techDisplayName(k) === techName)?.[1] || null;
  const modalities = myWaveform
    ? Object.entries(myWaveform)
        .map(([name, v]) => ({
          name,
          score: v.score,
          case_count: v.case_count,
          possible_points: v.possible_points,
          sparse: v.case_count < MIN_MODALITY_CASES,
        }))
        // .sort((a, b) => b.case_count - a.case_count || b.possible_points - a.possible_points)
        .sort((a, b) => {
          // Sparse tiles have no score — park them at the end rather than
          // letting a null sort to the top or bottom arbitrarily.
          if (a.score == null && b.score == null) return b.case_count - a.case_count;
          if (a.score == null) return 1;
          if (b.score == null) return -1;
          return b.score - a.score || b.case_count - a.case_count;
        })
    : [];
  const waveformCaseCount = modalities.length
    ? Math.max(...modalities.map(m => m.case_count))
    : 0;

  // ── Auto-generated improvement suggestions ──
  const suggestions = [];
  bottomChecks.filter(c => c.fail_rate >= 30 && c.total >= 3).slice(0, 3).forEach(c => {
    suggestions.push({
      priority: c.fail_rate >= 60 ? "high" : "medium",
      text: `"${c.question.substring(0, 70)}${c.question.length > 70 ? "…" : ""}" was missed in ${c.fail_count} of ${c.total} cases (${c.fail_rate}%). Review the documentation standard for this item.`,
    });
  });
  procList.filter(p => !p.sparse && p.avg_score < companyAvg - 5).slice(0, 2).forEach(p => {
    suggestions.push({
      priority: "medium",
      text: `${p.name} cases average ${p.avg_score}%, below the company average of ${companyAvg}%. Extra attention on this procedure type would close the gap.`,
    });
  });
  modalities.filter(m => !m.sparse && m.score != null && m.score < 85).slice(0, 2).forEach(m => {
    suggestions.push({
      priority: m.score < 80 ? "high" : "medium",
      text: `${m.name} waveform QA is at ${m.score}% across ${m.case_count} case(s) — the lowest of the monitored modalities. Worth reviewing ${m.name} baseline and event-handling technique.`,
    });
  });
  if (!suggestions.length) {
    suggestions.push({ priority: "none", text: "No specific focus areas identified — performance is consistent across procedures and checks in this period." });
  }

  return {
    techName, config, noData: false,
    total, passed, failed, avgScore, passRate, documentation, waveform, overall,
    companyAvg, prevScore, prevLabel: prevPeriod?.label || "the previous period",
    prevMonthly: !!prevPeriod?.monthly,
    procList, bottomChecks, modalities, waveformCaseCount,
    suggestions,
    docs: myDocs,
    companyName: config.companyName || "",
    generatedAt: new Date().toLocaleString(),
    reportType: "individual",
  };
}

// ── Shared PDF helpers ────────────────────────────────────────────────────────
const PDF_BASE_CSS = (color) => `
* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; font-size: 12px; color: #1e293b; line-height: 1.55; padding: 40px; max-width: 980px; margin: 0 auto; }
@media print { body { padding: 0; } .no-print { display: none !important; } h2 { page-break-after: avoid; } tr { page-break-inside: avoid; } }
.header { border-bottom: 3px solid ${color}; padding-bottom: 16px; margin-bottom: 22px; display: flex; justify-content: space-between; align-items: flex-end; }
.header-left .org { font-size: 10px; font-weight: 700; color: #64748b; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 5px; }
.header-left .title { font-size: 22px; font-weight: 700; color: ${color}; letter-spacing: -0.02em; }
.header-right { text-align: right; font-size: 10.5px; color: #94a3b8; font-family: monospace; line-height: 1.9; }
h2 { font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.08em; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin: 24px 0 10px; }
.stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 16px 0; }
.stat { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 7px; padding: 12px 14px; }
.stat-val { font-size: 28px; font-weight: 300; color: ${color}; line-height: 1; letter-spacing: -0.02em; }
.stat-label { font-size: 9.5px; color: #64748b; text-transform: uppercase; letter-spacing: 0.06em; margin-top: 4px; }
.stat-sub { font-size: 10.5px; color: #94a3b8; margin-top: 2px; }
table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 4px; }
th { background: #f1f5f9; padding: 6px 10px; text-align: left; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; font-weight: 600; }
td { padding: 6px 10px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
tr:last-child td { border-bottom: none; }
.pass { color: #10b981; font-weight: 600; } .fail { color: #ef4444; font-weight: 600; } .warn { color: #f59e0b; font-weight: 600; }
.bar-wrap { display: inline-block; width: 70px; height: 4px; background: #e2e8f0; border-radius: 2px; vertical-align: middle; margin-right: 6px; }
.bar-fill { height: 100%; border-radius: 2px; background: ${color}; }
.badge { display: inline-block; padding: 1px 7px; border-radius: 4px; font-size: 9.5px; font-weight: 700; font-family: monospace; }
.badge-pass { background: #dcfce7; color: #15803d; } .badge-fail { background: #fee2e2; color: #dc2626; }
.alert-box { background: #fef9c3; border: 1px solid #fde68a; border-radius: 6px; padding: 10px 14px; font-size: 11px; color: #78350f; margin: 8px 0; }
.info-box  { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; padding: 10px 14px; font-size: 11px; color: #1e40af; margin: 8px 0; }
.attestation { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 7px; padding: 16px 18px; margin: 14px 0; font-size: 12px; line-height: 1.7; color: #14532d; }
.sig-block { margin-top: 32px; border-top: 1px solid #e2e8f0; padding-top: 18px; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 30px; }
.sig-line { border-bottom: 1px solid #94a3b8; height: 36px; margin-bottom: 4px; }
.sig-label { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.06em; }
.footer { margin-top: 32px; font-size: 10px; color: #94a3b8; font-family: monospace; border-top: 1px solid #e2e8f0; padding-top: 12px; display: flex; justify-content: space-between; }
.print-btn { position: fixed; bottom: 20px; right: 20px; padding: 10px 22px; background: ${color}; color: white; border: none; border-radius: 7px; cursor: pointer; font-size: 13px; font-weight: 600; box-shadow: 0 4px 14px rgba(0,0,0,0.2); }
`;

function pdfHeader(data, rt, color) {
  const c = data.config || {};
  const period = c.startDate ? `${c.startDate} to ${c.endDate || "Present"}` : "All Time";
  const scopeLabel = data.reportType === "individual"
    ? `Individual QA Report — ${data.techName}`
    : c.team && c.team !== "ALL" ? `${teamLabel(c.team)} Team QA Report`
    : c.region && c.region !== "ALL" ? `${c.region} QA Report` : "Company-Level QA Report";
  const scope = [];
  if (Array.isArray(c.technologist) && c.technologist.length)
    scope.push(`Technologists: ${c.technologist.length} of ${data.techMatrix?.rows?.length ?? c.technologist.length} selected`);
  if (Array.isArray(c.facility) && c.facility.length) scope.push(`Facilities: ${c.facility.length} selected`);
  if (Array.isArray(c.procedureType) && c.procedureType.length) scope.push(`Procedures: ${c.procedureType.length} selected`);
  if (c.excludeDualTech) scope.push("Tech 1 + Tech 2 cases excluded");

  return `<div class="header">
    <div class="header-left">
      <div class="title">${data.companyName || "NeuroGen · IOM QA Portal"}</div>
      <div style="font-size:13px;color:#475569;font-weight:600;margin-top:3px">${scopeLabel}</div>
      ${scope.length ? `<div style="font-size:10.5px;color:#64748b;margin-top:5px">${scope.join(" · ")}</div>` : ""}
    </div>
    <div class="header-right">Period: ${period}<br/>Generated: ${data.generatedAt}<br/>Confidential — Internal Use Only</div>
  </div>`;
}

function pdfStats(data, color) {
  const pc = data.passRate >= 70 ? "#10b981" : data.passRate >= 50 ? "#f59e0b" : "#ef4444";
  return `<div class="stats"><div class="stat"><div class="stat-val" style="color:${color}">${data.total}</div><div class="stat-label">Total Cases</div><div class="stat-sub">${data.passed} passed · ${data.failed} failed</div></div><div class="stat"><div class="stat-val" style="color:${pc}">${data.passRate}%</div><div class="stat-label">Pass Rate</div></div><div class="stat"><div class="stat-val" style="color:${color}">${data.avgScore == null ? "&mdash;" : data.avgScore + "%"}</div><div class="stat-label">Avg QA Score</div></div><div class="stat"><div class="stat-val" style="color:${color}">${data.reviewRate}%</div><div class="stat-label">Failed Cases Reviewed</div><div class="stat-sub">${data.pending} pending</div></div></div>`;
}

// Group report headline cards. Mirrors the on-screen KPI row exactly, so a
// printed report and the preview can't disagree. Kept separate from pdfStats,
// which the accreditation and hospital builders still use.
function pdfStatsGroup(data, color) {
  const pct = (v) => (v == null ? "—" : `${v}%`);
  const card = (value, label, sub, valColor) =>
    `<div class="stat"><div class="stat-val" style="color:${valColor}">${value}</div>` +
    `<div class="stat-label">${label}</div>` +
    (sub ? `<div class="stat-sub">${sub}</div>` : "") + `</div>`;

  const overallColor = data.overall == null ? "#94a3b8" : tierFor(data.overall).color;
  const docColor     = data.documentation.score == null ? "#94a3b8" : tierFor(data.documentation.score).color;
  const waveColor    = data.waveform.score == null ? "#94a3b8" : tierFor(data.waveform.score).color;

  return `<div class="stats">
    ${card(pct(data.overall), "Overall Score", `${data.total} case${data.total !== 1 ? "s" : ""}`, overallColor)}
    ${card(pct(data.documentation.score), "Documentation",
           data.documentation.score == null ? "No documentation checks scored"
             : `${data.documentation.caseCount} case${data.documentation.caseCount !== 1 ? "s" : ""}`, docColor)}
    ${card(pct(data.waveform.score), "Waveform Analysis",
           data.waveform.score == null ? "No waveform QA reviewed in this period"
             : `${data.waveform.caseCount} case${data.waveform.caseCount !== 1 ? "s" : ""}`, waveColor)}
    ${card("—", "MSO Score", "Not yet available", "#94a3b8")}
  </div>`;
}

function pdfProcTable(data) {
  const rows = Object.entries(data.byProc).sort((a, b) => b[1].total - a[1].total).map(([pt, s]) =>
    `<tr><td>${pt}</td><td style="text-align:center">${s.total}</td><td class="pass" style="text-align:center">${s.passed}</td><td class="fail" style="text-align:center">${s.failed}</td><td style="text-align:center">${s.avg_score}%</td></tr>`
  ).join("") || "<tr><td colspan='6' style='color:#94a3b8;text-align:center'>No data</td></tr>";
  return `<h2>Procedure Type Breakdown</h2><table><thead><tr><th>Procedure</th><th>Total</th><th>Passed</th><th>Failed</th><th>Avg Score</th></tr></thead><tbody>${rows}</tbody></table>`;
}

// Group version: score and volume only. Pass rate is gone, since "pass" meant
// a zero-defect case and wasn't useful.
function pdfProcTableGroup(data) {
  const rows = Object.entries(data.byProc).sort((a, b) => b[1].avg_score - a[1].avg_score).map(([pt, s]) => {
    const c = tierFor(s.avg_score).color;
    return `<tr><td>${pt}</td><td style="text-align:center">${s.total}</td>` +
           `<td style="text-align:center;color:#94a3b8">${s.waveCases}/${s.total}</td>` +
           `<td><div class="bar-wrap"><div class="bar-fill" style="width:${Math.min(100, s.avg_score)}%;background:${c}"></div></div></td>` +
           `<td style="text-align:right;font-weight:600;color:${c}">${s.avg_score}%</td></tr>`;
  }).join("") || "<tr><td colspan='4' style='color:#94a3b8;text-align:center'>No data</td></tr>";
  return `<h2>By Procedure Type</h2><table><thead><tr><th>Procedure</th><th style="text-align:center">Cases</th><th style="text-align:center">Wave</th><th></th><th style="text-align:right">Avg Score</th></tr></thead><tbody>${rows}</tbody></table>`;
}

function pdfTechTable(data) {
  const rows = Object.entries(data.byTech).sort((a, b) => b[1].total - a[1].total).map(([t, s]) => {
    const cls = s.pass_rate >= 70 ? "pass" : s.pass_rate >= 50 ? "warn" : "fail";
    return `<tr><td>${t}</td><td style="text-align:center">${s.total}</td><td class="pass" style="text-align:center">${s.passed}</td><td class="fail" style="text-align:center">${s.failed}</td><td style="text-align:center">${s.avg_score}%</td><td class="${cls}" style="text-align:center">${s.pass_rate}%</td></tr>`;
  }).join("") || "<tr><td colspan='6' style='color:#94a3b8;text-align:center'>No data</td></tr>";
  return `<h2>Technologist Performance</h2><table><thead><tr><th>Technologist</th><th>Cases</th><th>Passed</th><th>Failed</th><th>Avg Score</th><th>Pass Rate</th></tr></thead><tbody>${rows}</tbody></table>`;
}

// Tech x month matrix, matching the on-screen version. Cells are pooled points.
function pdfTechMatrix(data) {
  const m = data.techMatrix;
  if (!m || !m.rows.length) return "";
  const head = m.monthLabels.map(l => `<th style="text-align:center">${l}</th>`).join("");
  const rows = m.rows.map(r => {
    const cells = r.cells.map(c => {
      if (c.score == null) return `<td style="text-align:center;color:#cbd5e1">—</td>`;
      const t = tierFor(c.score);
      return `<td style="text-align:center;font-weight:600;color:${t.color};background:${t.bg}">${c.score}%</td>`;
    }).join("");
    const rc = tierFor(r.score).color;
    return `<tr><td>${r.name}</td><td style="text-align:center;color:#94a3b8">${r.caseCount}</td>${cells}` +
           `<td style="text-align:right;font-weight:700;color:${rc}">${r.score}%</td></tr>`;
  }).join("");
  return `<h2>By Technologist</h2><table><thead><tr><th>Technologist</th><th style="text-align:center">Cases</th>${head}<th style="text-align:right">All</th></tr></thead><tbody>${rows}</tbody></table>`;
}

// Ranked bar chart. Bars scale from barFloor rather than zero, so real
// differences are visible instead of eight near-identical full bars.
function pdfTechBars(data) {
  const m = data.techMatrix;
  if (!m || !m.rows.length) return "";
  const floor = m.barFloor;
  const rows = [...m.rows].sort((a, b) => b.score - a.score).map(r => {
    const t = tierFor(r.score);
    const w = Math.max(2, Math.round(((r.score - floor) / (100 - floor)) * 100));
    return `<tr><td style="width:190px">${r.name}</td>` +
           `<td><div class="bar-wrap" style="width:100%"><div class="bar-fill" style="width:${w}%;background:${t.color}"></div></div></td>` +
           `<td style="text-align:right;width:70px;color:#94a3b8">${r.caseCount} case${r.caseCount !== 1 ? "s" : ""}</td>` +
           `<td style="text-align:right;width:50px;font-weight:700;color:${t.color}">${r.score}%</td></tr>`;
  }).join("");
  const legend = TIERS.map(t =>
    `<span style="margin-right:12px"><span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${t.color};margin-right:4px"></span>${t.label} ${t.range}</span>`
  ).join("");
  return `<h2>Technologist Ranking</h2><table><tbody>${rows}</tbody></table>` +
         `<div style="font-size:9.5px;color:#94a3b8;margin-top:6px">${legend}</div>`;
}

// Pooled monthly trend. Same geometry as the on-screen chart.
function pdfTrend(data) {
  const all = data.techMatrix?.trend || [];
  const trend = all.filter(t => t.score != null);
  if (trend.length < 2) return "";
  const W = 900, H = 190, PAD_L = 44, PAD_R = 44, PAD_T = 16, PAD_B = 34;
  const lo = Math.min(80, ...trend.map(t => t.score));
  const hi = 100;
  const x = i => PAD_L + (i / (trend.length - 1)) * (W - PAD_L - PAD_R);
  const y = s => PAD_T + (1 - (s - lo) / (hi - lo)) * (H - PAD_T - PAD_B);
  const anchor = i => (i === 0 ? "start" : i === trend.length - 1 ? "end" : "middle");

  const step = (hi - lo) <= 12 ? 2 : 5;
  const gridvals = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi; v += step) gridvals.push(v);

  const bands = gridvals.map(v => {
    const isTier = TIERS.some(t => t.min === v);
    const stroke = isTier ? tierFor(v).color : "#94a3b8";
    return `<line x1="${PAD_L}" x2="${W - PAD_R}" y1="${y(v)}" y2="${y(v)}" stroke="${stroke}" stroke-opacity="${isTier ? 0.5 : 0.6}" stroke-dasharray="3 3"/>` +
           `<text x="${PAD_L - 6}" y="${y(v) + 3}" text-anchor="end" font-size="9" fill="#94a3b8">${v}</text>`;
  }).join("");

  const pts = trend.map((t, i) => `${x(i)},${y(t.score)}`).join(" ");
  const marks = trend.map((t, i) =>
    `<circle cx="${x(i)}" cy="${y(t.score)}" r="3.5" fill="${tierFor(t.score).color}"/>` +
    `<text x="${x(i)}" y="${y(t.score) - 9}" text-anchor="${anchor(i)}" font-size="10" font-weight="600" fill="${tierFor(t.score).color}">${t.score}%</text>` +
    `<text x="${x(i)}" y="${H - 16}" text-anchor="${anchor(i)}" font-size="10" fill="#94a3b8">${t.label}</text>` +
    `<text x="${x(i)}" y="${H - 4}" text-anchor="${anchor(i)}" font-size="8.5" fill="#94a3b8">${t.caseCount} case${t.caseCount !== 1 ? "s" : ""}</text>`
  ).join("");

  return `<h2>Trend</h2><svg viewBox="0 0 ${W} ${H}" style="width:100%;height:auto;font-family:monospace">${bands}<polyline fill="none" stroke="#1d4ed8" stroke-width="2" stroke-linejoin="round" points="${pts}"/>${marks}</svg>` +
         `<div style="font-size:9.5px;color:#94a3b8;font-style:italic;margin-top:4px">Pooled score across all technologists in scope · axis starts at ${lo}%</div>`;
}

function pdfGaps(data) {
  if (!data.gaps?.length) return "";
  return `<div class="alert-box" style="margin-top:20px"><strong>⚠ Data Gaps / Notes:</strong><ul style="margin:6px 0 0;padding-left:18px;line-height:1.8">${data.gaps.map(g => `<li>${g}</li>`).join("")}</ul></div>`;
}

// Per-case scores for the case log. Documentation and Waveform are the two
// scored halves; Total pools their points rather than averaging the two, so a
// row reconciles with the Overall card at the top of the report.
export function caseScores(d) {
  const de = d.qa_earned   || 0, dp = d.qa_possible   || 0;
  const we = d.waveform_earned || 0, wp = d.waveform_possible || 0;
  return {
    doc:   dp ? Math.round((de / dp) * 100) : null,
    wave:  wp ? Math.round((we / wp) * 100) : null,
    total: (dp + wp) ? Math.round(((de + we) / (dp + wp)) * 100) : null,
  };
}

/* The case log, shared by the group and individual reports so both print the
   same columns. Grouped under each technologist's name in the group report;
   the individual report is one tech, where every header row would repeat the
   same name, so it lists the cases straight, newest first. */
function pdfCaseLog(docs, { title, groupByTech = true }) {
  const rows = [...(docs || [])]
    .sort((a, b) =>
      (groupByTech ? (a.technologist_name || "").localeCompare(b.technologist_name || "") : 0) ||
      new Date(b.processed_at || 0) - new Date(a.processed_at || 0))
    .map((d, i, arr) => {
      const s = caseScores(d);
      const cell = (v) => v == null
        ? `<td style="text-align:center;color:#cbd5e1">—</td>`
        : `<td style="text-align:center;font-weight:600;color:${tierFor(v).color}">${v}%</td>`;
      const newTech = groupByTech && (i === 0 || d.technologist_name !== arr[i-1].technologist_name);
      const header = newTech
        ? `<tr><td colspan="7" style="background:#f1f5f9;font-weight:700;font-size:10.5px;padding:5px 10px">${d.technologist_name || "Unknown"}</td></tr>`
        : "";
      return header +
        `<tr><td style="font-family:monospace;font-size:10px">${d.patient_id || d.file_name}</td>` +
        `<td>${d.processed_at ? new Date(d.processed_at).toLocaleDateString() : "—"}</td>` +
        `<td>${d.procedure_type}</td>` +
        cell(s.doc) + cell(s.wave) + cell(s.total) +
        `<td style="text-align:center">${pdfTierBadge(s.total)}</td></tr>`;
    }).join("");

  return `
    <h2>${title}</h2>
    <table><thead><tr><th>PID</th><th>Date</th><th>Procedure</th><th style="text-align:center">Doc</th><th style="text-align:center">Wave</th><th style="text-align:center">Total</th><th style="text-align:center">Tier</th></tr></thead><tbody>${rows}</tbody></table>`;
}

function pdfTierBadge(score) {
  if (score == null) return "—";
  const t = tierFor(score);
  return `<span style="display:inline-block;padding:1px 7px;border-radius:4px;font-size:9.5px;font-weight:600;background:${t.bg};color:${t.color};border:1px solid ${t.border};white-space:nowrap">${t.label}</span>`;
}

// ── Internal QA PDF ───────────────────────────────────────────────────────────
function buildInternalPDF(data, color) {
  const topRows = data.topFailed.map((c, i) =>
    `<tr><td style="text-align:center">${i+1}</td><td>${c.check_id}</td><td>${c.category}</td><td>${c.question.substring(0,80)}</td><td class="fail" style="text-align:center">${c.fail_count}</td><td style="text-align:center">${c.total}</td><td class="fail" style="text-align:center">${c.fail_rate}%</td></tr>`
  ).join("") || "<tr><td colspan='7' style='color:#94a3b8;text-align:center'>No failed checks</td></tr>";

  const actionHtml = data.actionItems.map((a, i) =>
    `<div style="padding:7px 0;border-bottom:1px solid #e2e8f0;display:flex;gap:10px;align-items:flex-start"><span style="background:${color};color:white;border-radius:50%;width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;font-size:9px;font-weight:700;flex-shrink:0;margin-top:1px">${i+1}</span><span style="font-size:11.5px">${a}</span></div>`
  ).join("");

  const unresolvedRows = data.unresolvedFailed.slice(0, 50).map(d =>
    `<tr><td style="font-family:monospace;font-size:10px">${d.file_name}</td><td>${d.processed_at ? new Date(d.processed_at).toLocaleDateString() : "—"}</td><td>${d.procedure_type}</td><td>${d.technologist_name}</td><td class="fail" style="text-align:center">${d.fail_count}</td></tr>`
  ).join("");

  return `
    ${pdfStatsGroup(data, color)}
    ${pdfProcTableGroup(data)}
    ${pdfTrend(data)}
    ${pdfTechBars(data)}
    ${pdfTechMatrix(data)}
    <h2>Top Failed QA Checks — Ranked by Frequency</h2>
    <table><thead><tr><th>#</th><th>Check ID</th><th>Category</th><th>Description</th><th>Failures</th><th>Cases</th><th>Rate</th></tr></thead><tbody>${topRows}</tbody></table>
    <h2>Recommended Action Items</h2>
    <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:7px;padding:14px 16px">${actionHtml}</div>
    ${pdfCaseLog(data.docs, { title: `Full Case Log — ${data.total} Cases` })}`;
}

// ── Accreditation PDF ─────────────────────────────────────────────────────────
function buildAccreditationPDF(data, color) {
  const catRows = Object.entries(data.catPerf).map(([cat, v]) => {
    const cls = v.rate >= 70 ? "pass" : v.rate >= 50 ? "warn" : "fail";
    return `<tr><td>${cat}</td><td style="text-align:center">${v.pass}</td><td style="text-align:center">${v.total-v.pass}</td><td style="text-align:center">${v.total}</td><td class="${cls}" style="text-align:center">${v.rate}%</td></tr>`;
  }).join("") || "<tr><td colspan='5' style='color:#94a3b8;text-align:center'>No data</td></tr>";

  const modRows = Object.entries(data.modalities).filter(([,v]) => v > 0).map(([mod, cnt]) =>
    `<tr><td>${mod}</td><td style="text-align:center">${cnt}</td><td style="text-align:center">${data.total ? Math.round((cnt/data.total)*100) : 0}%</td></tr>`
  ).join("") || "<tr><td colspan='3' style='color:#94a3b8'>Modality data requires document reprocessing</td></tr>";

  const techRows = Object.entries(data.byTech).sort((a,b) => b[1].total - a[1].total).map(([t, v]) =>
    `<tr><td>${t}</td><td style="text-align:center">${v.total}</td><td class="pass" style="text-align:center">${v.passed}</td><td class="fail" style="text-align:center">${v.failed||0}</td><td style="text-align:center">${v.avg_score}%</td><td class="${v.pass_rate>=80?"pass":v.pass_rate>=60?"warn":"fail"}" style="text-align:center">${v.pass_rate}%</td><td style="font-size:10px">${v.modalities}</td></tr>`
  ).join("") || "<tr><td colspan='7' style='color:#94a3b8;text-align:center'>No data</td></tr>";

  const caseRows = data.docs.sort((a,b) => new Date(b.processed_at||0) - new Date(a.processed_at||0)).slice(0,300).map(d =>
    `<tr><td>${d.processed_at ? new Date(d.processed_at).toLocaleDateString() : "—"}</td><td style="font-family:monospace;font-size:10px">${d.file_name}</td><td>${d.technologist_name}</td><td>${d.procedure_type}</td><td style="font-size:10px">${d.modalities_used||"—"}</td><td style="text-align:center"><span class="badge ${d.fail_count===0?"badge-pass":"badge-fail"}">${d.fail_count===0?"PASSED":"FAILED"}</span></td><td style="text-align:center">${d.qa_score}%</td><td style="font-size:10px">${d.reviewed_by||"—"}</td></tr>`
  ).join("");

  return `
    <div class="attestation"><strong style="display:block;margin-bottom:8px">Quality Assurance Program Attestation</strong>
    NeuroGen maintains a formal Quality Assurance program for all Intraoperative Neurophysiological Monitoring services. All IONM cases undergo structured documentation review against standardized QA criteria covering Digital Handshake, Documentation Standards, and Communication requirements. Failed checks are reviewed and dispositioned by qualified personnel. This report reflects QA activity for the stated period and is provided in support of accreditation and credentialing processes.</div>
    ${pdfStats(data, color)}
    <h2>Case Volume by Modality</h2>
    <table style="width:50%"><thead><tr><th>Modality</th><th>Cases</th><th>% of Total</th></tr></thead><tbody>${modRows}</tbody></table>
    <h2>QA Performance by Category</h2>
    <table><thead><tr><th>QA Category</th><th>Checks Passed</th><th>Checks Failed</th><th>Total</th><th>Compliance Rate</th></tr></thead><tbody>${catRows}</tbody></table>
    <h2>Technologist Performance Log (CNIM Documentation)</h2>
    <table><thead><tr><th>Technologist</th><th>Cases</th><th>Passed</th><th>Failed</th><th>Avg Score</th><th>Pass Rate</th><th>Modalities</th></tr></thead><tbody>${techRows}</tbody></table>
    <h2>Documentation Standards Compliance</h2>
    <div class="stats" style="grid-template-columns:repeat(3,1fr)">
      <div class="stat"><div class="stat-val" style="color:${color}">${data.docRates.start_rate}%</div><div class="stat-label">IONM START Documented</div></div>
      <div class="stat"><div class="stat-val" style="color:${color}">${data.docRates.stop_rate}%</div><div class="stat-label">IONM STOP Documented</div></div>
      <div class="stat"><div class="stat-val" style="color:${color}">${data.docRates.both_rate}%</div><div class="stat-label">Complete Timing</div></div>
    </div>
    <h2>Complete Case Log — ${data.total} Cases</h2>
    <table><thead><tr><th>Date</th><th>Document</th><th>Technologist</th><th>Procedure</th><th>Modalities</th><th>QA Result</th><th>Score</th><th>Reviewed By</th></tr></thead><tbody>${caseRows}</tbody></table>
    <div class="sig-block"><div><div class="sig-line"></div><div class="sig-label">Authorized Signatory</div></div><div><div class="sig-line"></div><div class="sig-label">Title / Credentials</div></div><div><div class="sig-line"></div><div class="sig-label">Date</div></div></div>`;
}

// ── Hospital PDF ──────────────────────────────────────────────────────────────
function buildHospitalPDF(data, color) {
  const facilityRows = Object.entries(data.byFacility).sort((a,b) => b[1].total - a[1].total).map(([f, v]) => {
    const cls = v.pass_rate >= 80 ? "pass" : "warn";
    return `<tr><td>${f}</td><td style="text-align:center">${v.total}</td><td class="pass" style="text-align:center">${v.passed}</td><td class="fail" style="text-align:center">${v.failed||0}</td><td class="${cls}" style="text-align:center">${v.pass_rate}%</td></tr>`;
  }).join("") || "<tr><td colspan='5' style='color:#94a3b8;text-align:center'>Facility data will appear for new documents processed after this update</td></tr>";

  const surgeonRows = Object.entries(data.bySurgeon).sort((a,b) => b[1].total - a[1].total).map(([s, v]) => {
    const cls = v.pass_rate >= 80 ? "pass" : "warn";
    return `<tr><td>${s}</td><td style="text-align:center">${v.total}</td><td class="pass" style="text-align:center">${v.passed}</td><td class="fail" style="text-align:center">${v.failed||0}</td><td class="${cls}" style="text-align:center">${v.pass_rate}%</td></tr>`;
  }).join("") || "<tr><td colspan='5' style='color:#94a3b8;text-align:center'>Surgeon data requires document reprocessing</td></tr>";

  const failedRows = data.docs.filter(d => d.fail_count > 0).sort((a,b) => new Date(b.processed_at||0) - new Date(a.processed_at||0)).slice(0,100).map(d => {
    const rev = ["REVIEWED","CLOSED"].includes(d.reviewer_status) ? "✓ Reviewed" : "⚠ Pending";
    const cls = ["REVIEWED","CLOSED"].includes(d.reviewer_status) ? "pass" : "fail";
    return `<tr><td>${d.processed_at ? new Date(d.processed_at).toLocaleDateString() : "—"}</td><td style="font-family:monospace;font-size:10px">${d.file_name}</td><td>${d.facility||"—"}</td><td>${d.surgeon_name}</td><td>${d.procedure_type}</td><td>${d.monitoring_neurologist}</td><td class="fail" style="text-align:center">${d.fail_count}</td><td class="${cls}" style="text-align:center">${rev}</td><td style="font-size:10px">${d.reviewed_by||"—"}</td></tr>`;
  }).join("");

  const allRows = data.docs.sort((a,b) => new Date(b.processed_at||0) - new Date(a.processed_at||0)).slice(0,300).map(d =>
    `<tr><td>${d.processed_at ? new Date(d.processed_at).toLocaleDateString() : "—"}</td><td>${d.facility||"—"}</td><td>${d.surgeon_name}</td><td>${d.procedure_type}</td><td>${d.technologist_name}</td><td>${d.monitoring_neurologist}</td><td style="font-size:10px">${d.modalities_used||"—"}</td><td style="text-align:center"><span class="badge ${d.fail_count===0?"badge-pass":"badge-fail"}">${d.fail_count===0?"PASSED":"FAILED"}</span></td><td style="text-align:center">${d.qa_score}%</td></tr>`
  ).join("");

  return `
    <div class="info-box">Services provided by NeuroGen. This report summarizes IONM quality assurance for the stated period. All figures reflect post-case documentation review against standardized QA criteria.</div>
    ${pdfStats(data, color)}
    <h2>⚠ Alert Cases</h2>
    <div class="alert-box">Alert case tracking is pending implementation of the manual alert flagging feature. Once enabled, cases with significant intraoperative events (signal loss, surgeon notification, anesthesia interventions) will appear here. These cases carry elevated litigation risk.</div>
    <h2>Cases by Facility</h2>
    <table><thead><tr><th>Facility</th><th>Total Cases</th><th>Passed</th><th>Failed</th><th>Pass Rate</th></tr></thead><tbody>${facilityRows}</tbody></table>
    <h2>Cases by Surgeon</h2>
    <table><thead><tr><th>Surgeon</th><th>Total Cases</th><th>Passed</th><th>Failed</th><th>Pass Rate</th></tr></thead><tbody>${surgeonRows}</tbody></table>
    ${pdfProcTable(data)}
    <h2>Failed Cases — QA Disposition (${data.docs.filter(d=>d.fail_count>0).length} Cases)</h2>
    ${data.unresolvedFailed.length ? `<div class="alert-box">⚠ ${data.unresolvedFailed.length} case(s) have unresolved failed checks pending reviewer action.</div>` : `<div class="info-box">✓ All failed cases have been reviewed and dispositioned.</div>`}
    <table><thead><tr><th>Date</th><th>Document</th><th>Facility</th><th>Surgeon</th><th>Procedure</th><th>Monitoring Neurologist</th><th>Failed Checks</th><th>Disposition</th><th>Reviewed By</th></tr></thead><tbody>${failedRows||"<tr><td colspan='9' style='color:#94a3b8;text-align:center'>No failed cases in this period</td></tr>"}</tbody></table>
    <h2>Complete Case Log — ${data.total} Cases</h2>
    <table><thead><tr><th>Date</th><th>Facility</th><th>Surgeon</th><th>Procedure</th><th>Technologist</th><th>Monitoring Neurologist</th><th>Modalities</th><th>QA Result</th><th>Score</th></tr></thead><tbody>${allRows}</tbody></table>`;
}

// ── Medicare PDF ──────────────────────────────────────────────────────────────
function buildMedicarePDF(data, color) {
  const timingRows = data.docs.sort((a,b) => new Date(b.processed_at||0) - new Date(a.processed_at||0)).slice(0,300).map(d => {
    const sc = d.ionm_start_time ? "pass" : "fail";
    const ec = d.ionm_stop_time  ? "pass" : "fail";
    return `<tr><td>${d.processed_at ? new Date(d.processed_at).toLocaleDateString() : "—"}</td><td style="font-family:monospace;font-size:10px">${d.file_name}</td><td>${d.procedure_type}</td><td class="${sc}" style="text-align:center">${d.ionm_start_time||"NOT FOUND"}</td><td class="${ec}" style="text-align:center">${d.ionm_stop_time||"NOT FOUND"}</td><td style="text-align:center">${d.monitoring_duration_mins!=null?d.monitoring_duration_mins+" min":"—"}</td><td style="text-align:center;font-weight:600">${d.billing_units_95940!=null?d.billing_units_95940+" units":"—"}</td><td>${d.technologist_name}</td><td style="font-size:10px">${d.monitoring_neurologist}</td></tr>`;
  }).join("");

  const gapRows = data.docGapCases.slice(0,100).map(d =>
    `<tr><td>${d.processed_at ? new Date(d.processed_at).toLocaleDateString() : "—"}</td><td style="font-family:monospace;font-size:10px">${d.file_name}</td><td>${d.procedure_type}</td><td class="${d.ionm_start_time?"pass":"fail"}" style="text-align:center">${d.ionm_start_time?"✓ "+d.ionm_start_time:"⚠ Missing"}</td><td class="${d.ionm_stop_time?"pass":"fail"}" style="text-align:center">${d.ionm_stop_time?"✓ "+d.ionm_stop_time:"⚠ Missing"}</td><td style="font-size:10px">${(d.documentation_gaps||[]).join("; ")}</td></tr>`
  ).join("") || "<tr><td colspan='6' class='pass' style='text-align:center'>All cases have complete timing documentation</td></tr>";

  return `
    <div class="info-box">This report documents IONM service compliance for CPT 95940 / G0453 billing purposes. IONM START and STOP times are extracted from QA check evidence fields. CPT billing codes and concurrent case conflict detection require USMON integration.</div>
    <h2>Compliance Summary</h2>
    <div class="stats">
      <div class="stat"><div class="stat-val" style="color:${data.docRates.start_rate>=90?"#10b981":"#ef4444"}">${data.docRates.start_rate}%</div><div class="stat-label">IONM START Documented</div><div class="stat-sub">${data.docRates.start_count} of ${data.total} cases</div></div>
      <div class="stat"><div class="stat-val" style="color:${data.docRates.stop_rate>=90?"#10b981":"#ef4444"}">${data.docRates.stop_rate}%</div><div class="stat-label">IONM STOP Documented</div><div class="stat-sub">${data.docRates.stop_count} of ${data.total} cases</div></div>
      <div class="stat"><div class="stat-val" style="color:${data.docRates.both_rate>=90?"#10b981":"#ef4444"}">${data.docRates.both_rate}%</div><div class="stat-label">Complete Timing (Billable)</div><div class="stat-sub">${data.docRates.both_count} of ${data.total} cases</div></div>
      <div class="stat"><div class="stat-val" style="color:${color}">${data.docGapCases.length}</div><div class="stat-label">Documentation Gaps</div></div>
    </div>
    <h2>⚠ Documentation Gap Cases — Billing Risk (${data.docGapCases.length})</h2>
    ${data.docGapCases.length ? `<div class="alert-box">These ${data.docGapCases.length} case(s) are missing IONM START or STOP time documentation. Incomplete timing may not support billing under CPT 95940 / G0453.</div>` : ""}
    <table><thead><tr><th>Date</th><th>Document</th><th>Procedure</th><th>IONM START</th><th>IONM STOP</th><th>Gaps Identified</th></tr></thead><tbody>${gapRows}</tbody></table>
    <h2>Per-Case Monitoring Time & Billing Units — ${data.total} Cases</h2>
    <div class="info-box">Duration = IONM START to IONM STOP. Billing units at 1 unit per 15 minutes per CMS rules for CPT 95940. Verify against actual documentation before claim submission.</div>
    <table><thead><tr><th>Date</th><th>Document</th><th>Procedure</th><th>IONM START</th><th>IONM STOP</th><th>Duration</th><th>95940 Units</th><th>Technologist</th><th>Monitoring Neurologist</th></tr></thead><tbody>${timingRows}</tbody></table>
    <div class="alert-box" style="margin-top:16px">⚠ Data Limitations: CPT codes and place of service require USMON. Concurrent case conflict detection requires USMON scheduling data. This report does not constitute legal or billing compliance advice — verify all claims with a qualified billing professional.</div>`;
}

// ── Build Individual PDF ───────────────────────────────────────────────────────────
function buildIndividualPDF(data, color) {
  if (data.noData) {
    return `<div class="alert-box">No cases found for ${data.techName} in this period.</div>`;
  }

  const delta = data.prevScore == null ? null : data.avgScore - data.prevScore;
  const deltaText = delta == null ? "—" : delta === 0 ? "0 pts"
    : `<span style="font-size:0.45em;vertical-align:middle;margin-right:5px;position:relative;top:-0.1em">${delta > 0 ? "▲" : "▼"}</span>${Math.abs(delta)} pts`;
  const deltaColor = delta == null || delta === 0 ? "#64748b" : delta > 0 ? "#1D9E75" : "#A32D2D";

  // Headline stats
  const pct = (v) => (v == null ? "—" : `${v}%`);
  const overallColor = data.overall == null ? "#94a3b8" : tierFor(data.overall).color;
  const docColor     = data.documentation.score == null ? "#94a3b8" : tierFor(data.documentation.score).color;
  const waveColor    = data.waveform.score == null ? "#94a3b8" : tierFor(data.waveform.score).color;

  const stats = `<div class="stats">
    <div class="stat"><div class="stat-val" style="color:${overallColor}">${pct(data.overall)}</div>
      <div class="stat-label">Overall Score</div>
      <div class="stat-sub">${data.total} case${data.total !== 1 ? "s" : ""}</div></div>
    <div class="stat"><div class="stat-val" style="color:${docColor}">${pct(data.documentation.score)}</div>
      <div class="stat-label">Documentation</div>
      <div class="stat-sub">${data.documentation.score == null ? "No documentation checks scored" : `${data.documentation.caseCount} case${data.documentation.caseCount !== 1 ? "s" : ""} scored`}</div></div>
    <div class="stat"><div class="stat-val" style="color:${waveColor}">${pct(data.waveform.score)}</div>
      <div class="stat-label">Waveform Analysis</div>
      <div class="stat-sub">${data.waveform.score == null ? "No waveform QA reviewed" : `${data.waveform.caseCount} case${data.waveform.caseCount !== 1 ? "s" : ""} reviewed`}</div></div>
    <div class="stat"><div class="stat-val" style="color:${deltaColor}">${deltaText}</div>
      <div class="stat-label">${data.prevMonthly ? "vs Last Month" : "vs Previous Period"}</div>
      <div class="stat-sub">${data.prevScore == null ? `No cases in ${data.prevLabel}` : `from ${data.prevScore}% in ${data.prevLabel}`}</div></div>
  </div>`;

  // Procedure bars
  const procRows = data.procList.map(p =>
    `<tr><td>${p.name}${p.sparse ? ` <span style="font-size:9px;color:#94a3b8">(few cases)</span>` : ""}</td><td style="text-align:center">${p.total}</td><td style="text-align:center;color:${tierFor(p.avg_score).color};font-weight:600">${p.avg_score}%</td></tr>`
  ).join("") || `<tr><td colspan="4" style="color:#94a3b8;text-align:center">No data</td></tr>`;

  // Modality treemap — proportional boxes as static HTML
  let modalityHtml;
  if (!data.modalities.length) {
    modalityHtml = `<div class="info-box">No waveform QA completed for this technologist in this period.</div>`;
  } else {
    const totalW = data.modalities.reduce((s, m) => s + m.case_count, 0) || 1;
    const boxes = data.modalities.map(m => {
      const c = m.sparse ? "#94a3b8" : tierFor(m.score).color;
      const pct = Math.max(9, Math.round((m.case_count / totalW) * 100));
      return `<div style="width:${pct}%;min-width:78px;box-sizing:border-box;padding:8px 10px;border:1px solid ${c}40;background:${c}14;border-radius:5px">
        <div style="font-size:10.5px;font-weight:700;color:${c}">${m.name}</div>
        <div style="font-size:17px;font-weight:300;color:${c};line-height:1.2">${m.sparse ? "—" : m.score + "%"}</div>
        <div style="font-size:9px;color:#94a3b8;font-family:monospace">${m.case_count} case${m.case_count !== 1 ? "s" : ""}</div>
      </div>`;
    }).join("");
    modalityHtml = `<div style="display:flex;flex-wrap:wrap;gap:5px">${boxes}</div>
      <div style="font-size:9.5px;color:#94a3b8;margin-top:6px;font-style:italic">Box width = case volume · based on ${data.waveformCaseCount} case(s) with waveform QA completed.</div>`;
    }

  // Bottom checks
  const checkRows = data.bottomChecks.map((c, i) =>
    `<tr><td style="text-align:center">${i+1}</td><td>${c.check_id}</td><td>${c.category}</td><td>${c.question.substring(0,80)}</td><td class="fail" style="text-align:center">${c.fail_count} of ${c.total}</td><td class="fail" style="text-align:center">${c.fail_rate}%</td></tr>`
  ).join("") || `<tr><td colspan="6" class="pass" style="text-align:center">No repeatedly missed checks — nothing to flag</td></tr>`;


  // Suggestions
  const sugHtml = data.suggestions.map((s, i) => {
    const c = s.priority === "high" ? "#A32D2D" : s.priority === "medium" ? "#854F0B" : "#1D9E75";
    return `<div style="padding:8px 0;border-bottom:1px solid #e2e8f0;display:flex;gap:10px;align-items:flex-start">
      <span style="background:${c};color:white;border-radius:50%;width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;font-size:9px;font-weight:700;flex-shrink:0;margin-top:1px">${i+1}</span>
      <span style="font-size:11.5px">${s.text}</span></div>`;
  }).join("");

  return `
    ${stats}
    <h2>Score by Procedure Type</h2>
    <table><thead><tr><th>Procedure</th><th>Cases</th><th>Avg Score</th></tr></thead><tbody>${procRows}</tbody></table>
    <h2>Modality Performance — Technical Knowledge</h2>
    ${modalityHtml}
    <h2>Most Frequently Missed Checks</h2>
    <table><thead><tr><th>#</th><th>Check ID</th><th>Category</th><th>Description</th><th>Missed</th><th>Rate</th></tr></thead><tbody>${checkRows}</tbody></table>
    <h2>Company Comparison</h2>
    <div style="display:flex;gap:10px;margin-bottom:10px">
      <div class="stat" style="flex:1"><div class="stat-val" style="color:${tierFor(data.avgScore).color}">${data.avgScore}%</div><div class="stat-label">${data.techName}</div></div>
      <div class="stat" style="flex:1"><div class="stat-val" style="color:#64748b">${data.companyAvg}%</div><div class="stat-label">Company Average</div></div>
    </div>
    <h2>Focus Areas</h2>
    <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:7px;padding:14px 16px">${sugHtml}</div>
    ${pdfCaseLog(data.docs, { title: `Case Log — ${data.total} Cases`, groupByTech: false })}`;
}

// ── Main PDF export ───────────────────────────────────────────────────────────
export function exportPDF(data) {
  const rt    = REPORT_TYPES.find(r => r.id === data.reportType);
  const color = rt.color;
  let body = "";
  if (data.reportType === "internal")      body = buildInternalPDF(data, color);
  if (data.reportType === "accreditation") body = buildAccreditationPDF(data, color);
  if (data.reportType === "hospital")      body = buildHospitalPDF(data, color);
  if (data.reportType === "medicare")      body = buildMedicarePDF(data, color);
  if (data.reportType === "individual")    body = buildIndividualPDF(data, color);

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${rt.label}</title><style>${PDF_BASE_CSS(color)}</style></head><body>
    ${pdfHeader(data, rt, color)}
    ${body}
    ${pdfGaps(data)}
    <div class="footer"><span>${data.companyName ? data.companyName + " · " : ""}Generated by NeuroGen Network Reporting</span><span>${data.generatedAt}</span></div>
    <button class="print-btn no-print" onclick="window.print()">⬇ Save as PDF</button>
    </body></html>`;

  const win = window.open("", "_blank");
  if (!win) { alert("Popup blocked — allow popups to export PDF."); return; }
  win.document.write(html);
  win.document.close();
}

// ── CSV export ────────────────────────────────────────────────────────────────
function csvRow(cells) { return cells.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","); }
function csvSec(title, headers, rows) { return [csvRow([title]), csvRow(headers), ...rows.map(r => csvRow(r)), csvRow([])].join("\n"); }

function exportCSV(data) {
  const rt     = REPORT_TYPES.find(r => r.id === data.reportType);
  const c      = data.config || {};
  const period = c.startDate ? `${c.startDate} to ${c.endDate || "Present"}` : "All Time";
  const scope  = [];
  if (c.team && c.team !== "ALL") scope.push(`Team: ${teamLabel(c.team)}`);
  if (c.region && c.region !== "ALL") scope.push(`Region: ${c.region}`);
  if (Array.isArray(c.technologist) && c.technologist.length) scope.push(`Technologists: ${c.technologist.join("; ")}`);
  if (Array.isArray(c.facility) && c.facility.length) scope.push(`Facilities: ${c.facility.join("; ")}`);
  if (Array.isArray(c.procedureType) && c.procedureType.length) scope.push(`Procedures: ${c.procedureType.join("; ")}`);
  if (c.excludeDualTech) scope.push("Tech 1 + Tech 2 cases excluded");

  const meta = [csvRow([data.companyName || ""]), csvRow([rt.label]), csvRow(["Generated by NeuroGen Network Reporting"]), csvRow([`Period: ${period}`]),
                ...scope.map(s => csvRow([s])), csvRow([`Generated: ${data.generatedAt}`]), csvRow([])].join("\n");

  const pct = (v) => (v == null ? "N/A" : `${v}%`);
  const summary = csvSec("SUMMARY", ["Metric","Value"], [
    ["Overall Score",       pct(data.overall)],
    ["Documentation Score", pct(data.documentation.score)],
    ["Documentation Cases Scored", data.documentation.caseCount],
    ["Waveform Score",      pct(data.waveform.score)],
    ["Waveform Cases Reviewed", data.waveform.caseCount],
    ["MSO Score",           "Not yet available"],
    ["Total Cases",         data.total],
  ]);

  const procSec = csvSec("BY PROCEDURE TYPE", ["Procedure","Cases","Avg Score"],
    Object.entries(data.byProc).sort((a,b) => b[1].avg_score - a[1].avg_score).map(([pt,v]) => [pt, v.total, `${v.avg_score}%`]));

  const tm = data.techMatrix || { monthLabels: [], rows: [] };
  const techSec = csvSec("BY TECHNOLOGIST", ["Technologist","Cases", ...tm.monthLabels, "All"],
    tm.rows.map(r => [r.name, r.caseCount, ...r.cells.map(c => (c.score == null ? "" : `${c.score}%`)), `${r.score}%`]));
  
  const trendSec = (tm.trend || []).length > 1
    ? csvSec("TREND", ["Month","Score","Cases"],
        tm.trend.map(t => [t.label, t.score == null ? "" : `${t.score}%`, t.caseCount]))
    : "";

  let typeContent = "";
  switch (data.reportType) {
    case "internal":
      typeContent = [
        csvSec("ACTION ITEMS", ["#","Item"], data.actionItems.map((a,i) => [i+1, a])),
        csvSec("TOP FAILED CHECKS", ["Rank","Check ID","Category","Description","Failures","Cases","Failure Rate"],
          data.topFailed.map((c,i) => [i+1, c.check_id, c.category, c.question, c.fail_count, c.total, `${c.fail_rate}%`])),
        procSec, techSec, trendSec,
        data.unresolvedFailed.length ? csvSec("UNRESOLVED FAILED CASES", ["Document","Date","Procedure","Technologist","Failed Checks"],
          data.unresolvedFailed.map(d => [d.file_name, d.processed_at ? new Date(d.processed_at).toLocaleDateString():"—", d.procedure_type, d.technologist_name, d.fail_count])) : "",
        csvSec("FULL CASE LOG", ["PID","Date","Procedure","Technologist","Doc","Wave","Total","Tier"],
          [...data.docs].sort((a,b) =>
            (a.technologist_name || "").localeCompare(b.technologist_name || "") ||
            new Date(b.processed_at || 0) - new Date(a.processed_at || 0)
          ).map(d => {
            const s = caseScores(d);
            return [d.patient_id || d.file_name,
                    d.processed_at ? new Date(d.processed_at).toLocaleDateString() : "—",
                    d.procedure_type, d.technologist_name,
                    s.doc   == null ? "" : `${s.doc}%`,
                    s.wave  == null ? "" : `${s.wave}%`,
                    s.total == null ? "" : `${s.total}%`,
                    s.total == null ? "" : tierFor(s.total).label];
          })),
      ].join("\n");
      break;
    case "accreditation":
      typeContent = [
        csvSec("MODALITY COVERAGE", ["Modality","Cases","% of Total"],
          Object.entries(data.modalities).filter(([,v])=>v>0).map(([m,v]) => [m, v, `${data.total?Math.round((v/data.total)*100):0}%`])),
        csvSec("QA CATEGORY COMPLIANCE", ["Category","Passed","Failed","Total","Rate"],
          Object.entries(data.catPerf).map(([cat,v]) => [cat, v.pass, v.total-v.pass, v.total, `${v.rate}%`])),
        csvSec("TECHNOLOGIST CASE LOG (CNIM)", ["Technologist","Cases","Passed","Failed","Avg Score","Pass Rate","Modalities"],
          Object.entries(data.byTech).sort((a,b) => b[1].total-a[1].total).map(([t,v]) => [t, v.total, v.passed, v.failed||0, `${v.avg_score}%`, `${v.pass_rate}%`, v.modalities])),
        csvSec("FULL CASE LOG", ["Date","Document","Technologist","Procedure","Modalities","QA Result","Score","Reviewed By"],
          data.docs.sort((a,b)=>new Date(b.processed_at||0)-new Date(a.processed_at||0)).map(d => [d.processed_at?new Date(d.processed_at).toLocaleDateString():"—", d.file_name, d.technologist_name, d.procedure_type, d.modalities_used||"—", d.fail_count===0?"PASSED":"FAILED", `${d.qa_score}%`, d.reviewed_by||""])),
      ].join("\n");
      break;
    case "hospital":
      typeContent = [
        csvSec("CASES BY FACILITY", ["Facility","Total","Passed","Failed","Pass Rate"],
          Object.entries(data.byFacility).sort((a,b)=>b[1].total-a[1].total).map(([f,v]) => [f, v.total, v.passed, v.failed||0, `${v.pass_rate}%`])),
        csvSec("CASES BY SURGEON", ["Surgeon","Total","Passed","Failed","Pass Rate"],
          Object.entries(data.bySurgeon).sort((a,b)=>b[1].total-a[1].total).map(([s,v]) => [s, v.total, v.passed, v.failed||0, `${v.pass_rate}%`])),
        procSec,
        data.unresolvedFailed.length ? csvSec("UNRESOLVED FAILED CASES", ["Document","Date","Facility","Surgeon","Procedure","Failed Checks"],
          data.unresolvedFailed.map(d => [d.file_name, d.processed_at?new Date(d.processed_at).toLocaleDateString():"—", d.facility||"—", d.surgeon_name, d.procedure_type, d.fail_count])) : "",
        csvSec("FULL CASE LOG", ["Date","Facility","Surgeon","Procedure","Technologist","Monitoring Neurologist","Modalities","QA Result","Score","Review Status"],
          data.docs.sort((a,b)=>new Date(b.processed_at||0)-new Date(a.processed_at||0)).map(d => [d.processed_at?new Date(d.processed_at).toLocaleDateString():"—", d.facility||"—", d.surgeon_name, d.procedure_type, d.technologist_name, d.monitoring_neurologist, d.modalities_used||"—", d.fail_count===0?"PASSED":"FAILED", `${d.qa_score}%`, d.reviewer_status.replace(/_/g," ")])),
      ].join("\n");
      break;
    case "medicare":
      typeContent = [
        csvSec("COMPLIANCE SUMMARY", ["Metric","Rate","Count"],
          [["IONM START Documented", `${data.docRates.start_rate}%`, data.docRates.start_count],
           ["IONM STOP Documented",  `${data.docRates.stop_rate}%`,  data.docRates.stop_count],
           ["Complete Timing",       `${data.docRates.both_rate}%`,  data.docRates.both_count],
           ["Documentation Gaps",   "—", data.docGapCases.length]]),
        csvSec("DOCUMENTATION GAP CASES", ["Date","Document","Procedure","IONM START","IONM STOP","Gaps"],
          data.docGapCases.map(d => [d.processed_at?new Date(d.processed_at).toLocaleDateString():"—", d.file_name, d.procedure_type, d.ionm_start_time||"MISSING", d.ionm_stop_time||"MISSING", (d.documentation_gaps||[]).join("; ")])),
        csvSec("PER-CASE BILLING LOG", ["Date","Document","Procedure","IONM START","IONM STOP","Duration (min)","95940 Units","Technologist","Monitoring Neurologist"],
          data.docs.sort((a,b)=>new Date(b.processed_at||0)-new Date(a.processed_at||0)).map(d => [d.processed_at?new Date(d.processed_at).toLocaleDateString():"—", d.file_name, d.procedure_type, d.ionm_start_time||"—", d.ionm_stop_time||"—", d.monitoring_duration_mins!=null?d.monitoring_duration_mins:"—", d.billing_units_95940!=null?d.billing_units_95940:"—", d.technologist_name, d.monitoring_neurologist])),
      ].join("\n");
      break;
  }

  const gapSec = data.gaps.length ? [csvRow(["DATA GAPS / NOTES"]), ...data.gaps.map(g => csvRow([g]))].join("\n") : "";
  const csv  = [meta, summary, typeContent, gapSec].filter(Boolean).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  const scopeVal  = c.team && c.team !== "ALL" ? teamLabel(c.team) + "_Team"
                  : c.region && c.region !== "ALL" ? c.region : "";
  const regionTag = scopeVal ? "_" + scopeVal.replace(/[\s/]+/g, "_") : "";
  a.download = `${rt.label.replace(/[\s/]+/g,"_")}${regionTag}_${c.startDate||"all"}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ background:"var(--white)", border:"1px solid var(--border)", borderRadius:8, padding:"14px 16px" }}>
      <div style={{ fontSize:26, fontWeight:300, color:color||"var(--text-primary)", lineHeight:1, letterSpacing:"-0.02em" }}>{value}</div>
      <div style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginTop:4 }}>{label}</div>
      {sub && <div style={{ fontSize:11, color:"var(--text-tertiary)", marginTop:2 }}>{sub}</div>}
    </div>
  );
}

// ── Treemap component ─────────────────────────────────────────────────────────────────
function ModalityTreemap({ modalities, caseCount, loading }) {
  if (!modalities.length) {
    return (
      <div style={{ padding:"20px 16px", background:"var(--gray-50)", border:"1px dashed var(--border)", borderRadius:6, fontSize:12.5, color:"var(--text-tertiary)", textAlign:"center" }}>
        {loading ? "Loading…" : "No waveform QA reviewed for this technologist in this period."}
      </div>
    );
  }

  const totalWeight = modalities.reduce((s, m) => s + m.case_count, 0) || 1;

  // Pack into rows of ~equal total weight so boxes stay roughly square.
  const rows = [];
  let row = [], rowWeight = 0;
  const targetPerRow = totalWeight / Math.max(1, Math.round(Math.sqrt(modalities.length)));
  modalities.forEach(m => {
    row.push(m); rowWeight += m.case_count;
    if (rowWeight >= targetPerRow) { rows.push(row); row = []; rowWeight = 0; }
  });
  if (row.length) rows.push(row);

  return (
    <div>
      <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
        {rows.map((r, ri) => {
          const rowTotal = r.reduce((s, m) => s + m.case_count, 0) || 1;
          return (
            <div key={ri} style={{ display:"flex", gap:4, height:76 }}>
              {r.map(m => {
                const t = m.sparse ? tierFor(null) : tierFor(m.score);
                return (
                  <div key={m.name} title={`${m.name} — ${m.case_count} case(s)${m.sparse ? " (too few to score)" : `, ${m.score}%`}`}
                    style={{
                      flex: m.case_count / rowTotal,
                      background: t.bg, border: `1px solid ${t.border}`, borderRadius: 5,
                      padding: "8px 10px", display: "flex", flexDirection: "column",
                      justifyContent: "space-between", overflow: "hidden", minWidth: 54,
                    }}>
                    <div style={{ fontSize:11, fontWeight:600, color:t.color, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{m.name}</div>
                    <div>
                      <div style={{ fontSize:m.sparse ? 13 : 19, fontWeight:m.sparse ? 400 : 300, color:t.color, lineHeight:1.1 }}>
                        {m.sparse ? "—" : `${m.score}%`}
                      </div>
                      <div style={{ fontSize:9.5, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", marginTop:1 }}>
                        {m.case_count} case{m.case_count !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <div style={{ display:"flex", gap:12, marginTop:8, flexWrap:"wrap", alignItems:"center" }}>
        {TIERS.map(t => (
          <span key={t.id} style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:10.5, color:"var(--text-tertiary)" }}>
            <span style={{ width:9, height:9, borderRadius:2, background:t.color, display:"inline-block" }}/>
            {t.label} {t.range}
          </span>
        ))}
      </div>
      <div style={{ fontSize:10.5, color:"var(--text-tertiary)", fontStyle:"italic", marginTop:6 }}>
        Box size = case volume · based on {caseCount} case{caseCount !== 1 ? "s" : ""} with waveform QA completed
      </div>
    </div>
  );
}

// hideExport: the technologist's My Monthly Report puts its own Export PDF
// button below its case list, so it turns this one off.
export function IndividualPreview({ data, color, waveformLoading, onExportPDF, generating, hideExport = false }) {
  if (!data) {
    return (
      <div style={{ padding:"32px 24px", textAlign:"center" }}>
        <div style={{ fontSize:14, fontWeight:600, color:"var(--text-secondary)", marginBottom:6 }}>Select a technologist</div>
        <div style={{ fontSize:13, color:"var(--text-tertiary)" }}>Choose a technologist above to generate their individual report.</div>
      </div>
    );
  }
  if (data.noData) {
    return (
      <div style={{ padding:"32px 24px", textAlign:"center" }}>
        <div style={{ fontSize:14, fontWeight:600, color:"var(--text-secondary)", marginBottom:6 }}>No cases found</div>
        <div style={{ fontSize:13, color:"var(--text-tertiary)" }}>{data.techName} has no cases in the selected period.</div>
      </div>
    );
  }

  const myTier   = tierFor(data.avgScore);

  return (
    <div style={{ padding:"14px 18px" }}>
      {/* Headline */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:18 }}>
        <div style={{ background:"var(--white)", border:"1px solid var(--border)", borderRadius:8, padding:"14px 16px" }}>
          <div style={{ fontSize:26, fontWeight:300, color:tierFor(data.overall).color, lineHeight:1, letterSpacing:"-0.02em" }}>
            {data.overall == null ? "—" : `${data.overall}%`}
          </div>
          <div style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginTop:4 }}>Overall Score</div>
          <div style={{ fontSize:11, color:tierFor(data.overall).color, fontWeight:600, marginTop:2 }}>{tierFor(data.overall).label}</div>
        </div>
        <StatCard label="Documentation"
          value={data.documentation.score == null ? "—" : `${data.documentation.score}%`}
          color={tierFor(data.documentation.score).color}
          sub={data.documentation.score == null
            ? "No documentation checks scored"
            : `${data.documentation.caseCount} case${data.documentation.caseCount !== 1 ? "s" : ""}`} />
        <StatCard label="Waveform Analysis"
          value={data.waveform.score == null ? "—" : `${data.waveform.score}%`}
          color={tierFor(data.waveform.score).color}
          sub={data.waveform.score == null
                          ? "No waveform QA reviewed in this period"
                          : `${data.waveform.caseCount} case${data.waveform.caseCount !== 1 ? "s" : ""}`} />
        {(() => {
          const d = data.prevScore == null ? null : data.avgScore - data.prevScore;
          return (
            <StatCard label={data.prevMonthly ? "vs Last Month" : "vs Previous Period"}
              value={d == null ? "—" : d === 0 ? "0 pts" : <span><span style={{ fontSize: "0.45em", verticalAlign: "middle", marginRight: 5, position: "relative", top: "-0.1em" }}>{d > 0 ? "▲" : "▼"}</span>{Math.abs(d)} pts</span>}
              color={d == null || d === 0 ? "var(--text-secondary)" : d > 0 ? "#1D9E75" : "#A32D2D"}
              sub={data.prevScore == null ? `No cases in ${data.prevLabel}` : `from ${data.prevScore}% in ${data.prevLabel}`} />
          );
        })()}
      </div>

      {/* Procedure type */}
      <div style={{ marginBottom:18 }}>
        <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Score by Procedure Type</div>
        <div style={{ background:"var(--gray-50)", borderRadius:6, border:"1px solid var(--border)", overflow:"hidden" }}>
          {data.procList.map(p => {
            const t = tierFor(p.avg_score);
            return (
              <div key={p.name} style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 12px", borderBottom:"1px solid var(--border)" }}>
                <span style={{ flex:1, fontSize:12, color:"var(--text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {p.name}
                  {p.sparse && <span style={{ fontSize:10, color:"var(--text-tertiary)", marginLeft:6 }}>few cases</span>}
                </span>
                <span style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", flexShrink:0 }}>{p.total} case{p.total !== 1 ? "s" : ""}</span>
                <span style={{ fontSize:10.5, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", flexShrink:0, width:64, textAlign:"right" }}
                  title="Cases with waveform QA reviewed">{p.waveCases}/{p.total} wave</span>
                <div style={{ width:90, height:5, background:"var(--gray-200)", borderRadius:3, flexShrink:0, overflow:"hidden" }}>
                  <div style={{ width:`${p.avg_score}%`, height:"100%", borderRadius:3, background:t.color }}/>
                </div>
                <span style={{ fontSize:11.5, fontFamily:"var(--font-mono)", fontWeight:600, color:t.color, flexShrink:0, minWidth:34 }}>{p.avg_score}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modality treemap */}
      <div style={{ marginBottom:18 }}>
        <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Modality Performance — Waveform QA</div>
        <ModalityTreemap modalities={data.modalities} caseCount={data.waveformCaseCount} loading={waveformLoading} />
      </div>

      {/* Bottom checks */}
      <div style={{ marginBottom:18 }}>
        <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Most Frequently Missed Checks</div>
        {data.bottomChecks.length ? (
          <div style={{ background:"var(--gray-50)", borderRadius:6, border:"1px solid var(--border)", overflow:"hidden" }}>
            {data.bottomChecks.map(c => (
              <div key={c.check_id} style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 12px", borderBottom:"1px solid var(--border)" }}>
                <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", flexShrink:0, minWidth:40 }}>{c.check_id}</span>
                <span style={{ flex:1, fontSize:12, color:"var(--text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.question}</span>
                <span style={{ fontSize:10.5, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", flexShrink:0 }}>{c.fail_count}/{c.total}</span>
                <span style={{ fontSize:11.5, fontFamily:"var(--font-mono)", fontWeight:600, color:tierFor(100 - c.fail_rate).color, flexShrink:0, minWidth:34 }}>{c.fail_rate}%</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding:"14px 16px", background:"#ecfdf5", border:"1px solid #a7f3d0", borderRadius:6, fontSize:12.5, color:"#065f46" }}>
            No repeatedly missed checks in this period.
          </div>
        )}
      </div>

      {/* Company comparison — anonymized */}
      <div style={{ marginBottom:18 }}>
        <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>
          Company Comparison
        </div>
        <div style={{ display:"flex", gap:10, marginBottom:10 }}>
          <div style={{ flex:1, background:"var(--white)", border:"1px solid var(--border)", borderRadius:8, padding:"12px 14px" }}>
            <div style={{ fontSize:22, fontWeight:300, color:myTier.color, lineHeight:1 }}>{data.avgScore}%</div>
            <div style={{ fontSize:10.5, color:"var(--text-tertiary)", marginTop:3 }}>{data.techName}</div>
          </div>
          <div style={{ flex:1, background:"var(--white)", border:"1px solid var(--border)", borderRadius:8, padding:"12px 14px" }}>
            <div style={{ fontSize:22, fontWeight:300, color:"var(--text-secondary)", lineHeight:1 }}>{data.companyAvg}%</div>
            <div style={{ fontSize:10.5, color:"var(--text-tertiary)", marginTop:3 }}>Company average</div>
          </div>
        </div>
      </div>

      {/* Suggestions */}
      <div style={{ marginBottom:16 }}>
        <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Focus Areas</div>
        <div style={{ background:"var(--gray-50)", borderRadius:6, border:"1px solid var(--border)", overflow:"hidden" }}>
          {data.suggestions.map((s, i) => {
            const c = s.priority === "high" ? "#A32D2D" : s.priority === "medium" ? "#854F0B" : "#1D9E75";
            return (
              <div key={i} style={{ display:"flex", gap:10, padding:"9px 12px", borderBottom:"1px solid var(--border)", alignItems:"flex-start" }}>
                <span style={{ background:c, color:"white", borderRadius:"50%", width:17, height:17, display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, flexShrink:0, marginTop:1 }}>{i+1}</span>
                <span style={{ fontSize:12, color:"var(--text-primary)", lineHeight:1.55 }}>{s.text}</span>
              </div>
            );
          })}
        </div>
      </div>

      {!hideExport && (
      <div style={{ display:"flex", gap:10, paddingTop:4 }}>
        <button className="btn btn-primary" onClick={onExportPDF} disabled={generating} style={{ display:"flex", alignItems:"center", gap:7 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"/></svg>
          Export PDF
        </button>
      </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Audit() {
  const [rawData,         setRawData]         = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [fetchError,      setFetchError]      = useState(null);
  const [reportType,      setReportType]      = useState("internal");
  const [preset,          setPreset]          = useState("last_month");
  const [config,          setConfig]          = useState({ ...getPresetDates("last_month"), procedureType:[], technologist:[], facility:[], region:"ALL", team:"ALL", companyName:"", excludeDualTech:false });
  const [reportData,      setReportData]      = useState(null);
  const [generating,      setGenerating]      = useState(false);
  const [selectedTech,    setSelectedTech]    = useState("");
  const [waveformSummary, setWaveformSummary] = useState(null);
  const [individualData,  setIndividualData]  = useState(null);

  const selType  = REPORT_TYPES.find(r => r.id === reportType);

  useEffect(() => {
    setLoading(true);
    getAuditData()
      .then(d => {
        setRawData(normalizeDocs(d));
        setConfig(prev => ({ ...prev, companyName: d.company_name || "" }));
        setFetchError(null);
      })
      .catch(e => setFetchError(e.message || "Failed to load audit data"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setWaveformSummary(undefined);
    getWaveformTechSummary("", config.startDate, config.endDate, config.region, "", config.excludeDualTech)
      .then(setWaveformSummary)
      .catch(() => setWaveformSummary(null));
  }, [config.startDate, config.endDate, config.region, config.excludeDualTech]);

  useEffect(() => {
    if (!rawData?.documents?.length) return;
    const filtered = filterDocs(rawData.documents, config);
    setReportData(computeReportData(filtered, reportType, config, waveformSummary));
  }, [rawData, reportType, config, waveformSummary]);

  useEffect(() => {
    const allowed = selType?.filters || [];
    setConfig(prev => ({
      ...prev,
      technologist:  allowed.includes("technologist")  ? prev.technologist  : [],
      team:          allowed.includes("team")          ? prev.team          : "ALL",
      region:        allowed.includes("region")        ? prev.region        : "ALL",
      facility:      allowed.includes("facility")      ? prev.facility      : [],
      procedureType: allowed.includes("procedureType") ? prev.procedureType : [],
    }));
  }, [reportType, selType]);

  useEffect(() => {
    if (reportType !== "individual" || !rawData?.documents?.length || !selectedTech) {
      setIndividualData(null);
      return;
    }
    const windowDocs = filterDocs(rawData.documents, { ...config, technologist: [] });
    const prev = previousPeriod(config.startDate, config.endDate);
    const prevDocs = prev ? filterDocs(rawData.documents, { ...config, startDate: prev.startDate, endDate: prev.endDate, technologist: [] }) : [];
    setIndividualData(computeIndividualData(windowDocs, selectedTech, config, waveformSummary, prevDocs));
  }, [rawData, reportType, config, selectedTech, waveformSummary]);

  const allDocs    = rawData?.documents || [];
  // Options follow the region or team, so picking one narrows the tech list to its techs.
  const regionDocs = allDocs.filter(d =>
    (config.region === "ALL" || d.region === config.region) &&
    (!config.team || config.team === "ALL" ||
     (config.team === UNASSIGNED_TEAM ? !d.team : d.team === config.team)));

  const procedureOpts = Array.from(new Set(regionDocs.map(d => d.procedure_type).filter(Boolean))).sort();
  const techOpts      = Array.from(new Set(regionDocs.map(d => d.technologist_name).filter(t => t && t !== "Unknown"))).sort();
  const facilityOpts  = Array.from(new Set(regionDocs.map(d => d.facility).filter(f => f && f !== "Unknown"))).sort();
  const regionOpts    = ["ALL", ...Array.from(new Set(allDocs.map(d => d.region).filter(r => r && r !== "Unknown"))).sort()];
  const teamOpts      = ["ALL", ...Array.from(new Set(allDocs.map(d => d.team).filter(Boolean))).sort(),
                         ...(allDocs.some(d => !d.team) ? [UNASSIGNED_TEAM] : [])];
  
  const handlePreset = useCallback((p) => { setPreset(p); setConfig(prev => ({ ...prev, ...getPresetDates(p) })); }, []);
  const handleConfig = useCallback((k, v) => {
    if (k === "startDate" || k === "endDate") setPreset("custom");
    if (k === "region" || k === "team") setSelectedTech("");
    // Changing region or team invalidates any tech/facility picks that belonged to the old one.
    setConfig(prev => ({ ...prev, [k]: v, ...(k === "region" || k === "team" ? { technologist: [], facility: [] } : {}) }));
  }, []);

  const doExportPDF = useCallback(() => {
    const d = reportType === "individual" ? individualData : reportData;
    if (!d || d.noData || (reportType !== "individual" && !d.total)) return;
    setGenerating(true);
    try { exportPDF(d); } finally { setGenerating(false); }
  }, [reportData, individualData, reportType]);
  const doExportCSV = useCallback(() => { if (!reportData?.total) return; setGenerating(true); try { exportCSV(reportData); } finally { setGenerating(false); } }, [reportData]);

  const rawColor = selType?.color || "#2563eb";

  return (
    <>
      <div className="page-header">
        <div className="page-title-group">
          <div className="page-title">NP Reports</div>
          <div className="page-subtitle">Generate and export compliance and QA audit reports</div>
        </div>
      </div>

      <div className="page-body" style={{ maxWidth:920 }}>

        {loading && <div style={{ padding:"48px 0", textAlign:"center", color:"var(--text-tertiary)", fontFamily:"var(--font-mono)", fontSize:13 }}>Loading audit data…</div>}

        {!loading && fetchError && (
          <div style={{ padding:"14px 16px", background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.25)", borderRadius:8, fontSize:13, color:"var(--error)" }}>
            Failed to load: {fetchError}
            <button className="btn btn-ghost btn-sm" style={{ marginLeft:12 }} onClick={() => { setLoading(true); getAuditData().then(d=>setRawData(normalizeDocs(d))).catch(e=>setFetchError(e.message)).finally(()=>setLoading(false)); }}>Retry</button>
          </div>
        )}

        {!loading && !fetchError && !allDocs.length && (
          <div className="card"><div style={{ padding:"48px 24px", textAlign:"center" }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--gray-300)" strokeWidth="1.5" style={{ marginBottom:14 }}><path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
            <div style={{ fontSize:14, fontWeight:600, color:"var(--text-secondary)", marginBottom:6 }}>No documents yet</div>
            <div style={{ fontSize:13, color:"var(--text-tertiary)" }}>Upload and process documents to generate audit reports.</div>
          </div></div>
        )}

        {!loading && !fetchError && allDocs.length > 0 && (
          <>
            {/* Step 1 — Report type */}
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>1 · Select Report Type</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10 }}>
                {REPORT_TYPES.map(rt => {
                  const sel = reportType === rt.id;
                  return (
                    <button key={rt.id} onClick={() => setReportType(rt.id)} style={{ padding:"14px 16px", borderRadius:8, border:`1.5px solid ${sel ? rt.color : "var(--border)"}`, background:sel ? rt.bgColor : "var(--white)", cursor:"pointer", textAlign:"left", transition:"all 0.12s", display:"flex", alignItems:"flex-start", gap:12 }}>
                      <span style={{ color:sel ? rt.color : "var(--text-tertiary)", marginTop:1, flexShrink:0 }}>{rt.icon}</span>
                      <div>
                        <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                          <span style={{ fontSize:13, fontWeight:600, color:sel ? rt.color : "var(--text-primary)" }}>{rt.label}</span>
                          {rt.subLabel && <span style={{ fontSize:10, fontFamily:"var(--font-mono)", background:rt.bgColor, color:rt.color, border:`1px solid ${rt.borderColor}`, padding:"1px 6px", borderRadius:3 }}>{rt.subLabel}</span>}
                          {rt.limitation && <span style={{ fontSize:10, fontFamily:"var(--font-mono)", background:"#fffbeb", color:"#92400e", border:"1px solid #fde68a", padding:"1px 6px", borderRadius:3 }}>Limited data</span>}
                        </div>
                        <div style={{ fontSize:12, color:"var(--text-tertiary)", marginTop:3 }}>{rt.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {selType?.limitation && (
              <div style={{ padding:"10px 14px", background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, fontSize:12.5, color:"#78350f", marginBottom:16, lineHeight:1.6 }}>
                <strong>⚠ Limited data available:</strong> {selType.limitation}
              </div>
            )}

            {/* Step 2 — Configure */}
            <div className="card" style={{ marginBottom:16, overflow:"visible" }}>
              <div className="card-header">
                <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em" }}>2 · Configure Report</div>
              </div>
              <div style={{ padding:"14px 18px", display:"flex", flexDirection:"column", gap:14 }}>
                <div>
                  <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Date Range</div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:10 }}>
                    {PRESETS.map(p => (
                      <button key={p.id} onClick={() => handlePreset(p.id)} style={{ padding:"4px 12px", borderRadius:20, fontSize:12, fontFamily:"var(--font-mono)", cursor:"pointer", border:`1px solid ${preset===p.id ? rawColor : "var(--border-strong)"}`, background:preset===p.id ? selType?.bgColor||"var(--blue-50)" : "var(--white)", color:preset===p.id ? rawColor : "var(--text-secondary)", fontWeight:preset===p.id ? 600 : 400 }}>{p.label}</button>
                    ))}
                  </div>
                  <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:11, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)", marginBottom:4 }}>Quarter</div>
                      <select value={preset.startsWith("q:") ? preset.slice(2) : ""} style={{ width:"100%", fontSize:13 }}
                        onChange={e => { const v = e.target.value; if (!v) return; setPreset("q:" + v); setConfig(prev => ({ ...prev, ...quarterDates(v) })); }}>
                        <option value="">Pick a quarter…</option>
                        {quarterOptions().map(q => <option key={q} value={q}>{q}</option>)}
                      </select>
                    </div>
                    <div style={{ flex:1 }}><div style={{ fontSize:11, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)", marginBottom:4 }}>From</div><input type="date" value={config.startDate} onChange={e => handleConfig("startDate", e.target.value)} style={{ width:"100%", fontSize:13 }}/></div>
                    <div style={{ color:"var(--text-tertiary)", marginTop:18 }}>→</div>
                    <div style={{ flex:1 }}><div style={{ fontSize:11, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)", marginBottom:4 }}>To</div><input type="date" value={config.endDate} onChange={e => handleConfig("endDate", e.target.value)} style={{ width:"100%", fontSize:13 }}/></div>
                  </div>
                  {config.startDate && config.endDate && new Date(config.startDate) > new Date(config.endDate) && (
                    <div style={{ marginTop:6, fontSize:12, color:"var(--error)", fontFamily:"var(--font-mono)" }}>⚠ Start date must be before end date</div>
                  )}
                </div>

                <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                  <span style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginRight:2 }}>Filters</span>

                  {selType?.filters?.includes("team") && (
                    <select value={config.team} onChange={e => handleConfig("team", e.target.value)}
                      style={{ width:"auto", flex:"0 0 auto", fontSize:11, fontFamily:"var(--font-mono)", padding:"5px 9px", borderRadius:6, border:"1px solid var(--border)", background:"var(--white)", color:"var(--text-secondary)", cursor:"pointer" }}>
                      {teamOpts.map(o => <option key={o} value={o}>{o === "ALL" ? "All Teams" : o === UNASSIGNED_TEAM ? "Unassigned" : o}</option>)}
                    </select>
                  )}

                  {selType?.filters?.includes("region") && (
                    <select value={config.region} onChange={e => handleConfig("region", e.target.value)}
                      style={{ width:"auto", flex:"0 0 auto", fontSize:11, fontFamily:"var(--font-mono)", padding:"5px 9px", borderRadius:6, border:"1px solid var(--border)", background:"var(--white)", color:"var(--text-secondary)", cursor:"pointer" }}>
                      {regionOpts.map(o => <option key={o} value={o}>{o === "ALL" ? "All Regions" : o}</option>)}
                    </select>
                  )}

                  {selType?.filters?.includes("technologist") && (
                    <FilterDropdown label="All Technologists" selected={config.technologist}
                      options={techOpts} onChange={v => handleConfig("technologist", v)} width={170} selectAll />
                  )}

                  {selType?.filters?.includes("facility") && (
                    <FilterDropdown label="All Facilities" selected={config.facility}
                      options={facilityOpts} onChange={v => handleConfig("facility", v)} width={160} />
                  )}

                  {selType?.filters?.includes("procedureType") && (
                    <FilterDropdown label="All Procedures" selected={config.procedureType}
                      options={procedureOpts} onChange={v => handleConfig("procedureType", v)} width={160} />
                  )}

                  {selType?.requiresTech && (
                    <span style={{ display:"flex", alignItems:"center", gap:5 }}>
                      <FilterDropdown label="Select a technologist…" selected={selectedTech ? [selectedTech] : []}
                        options={techOpts} onChange={v => setSelectedTech(v[v.length - 1] || "")} width={200} />
                      {!selectedTech && <span style={{ color:"var(--error)", fontSize:13 }}>*</span>}
                    </span>
                  )}

                  <label onClick={() => handleConfig("excludeDualTech", !config.excludeDualTech)}
                    style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-secondary)", cursor:"pointer" }}
                    title="Leave out cases with a second technologist, so Tech 1 is not graded on Tech 2's work">
                    <Checkbox checked={!!config.excludeDualTech} onChange={v => handleConfig("excludeDualTech", v)} />
                    Exclude Tech 1 + Tech 2 cases
                  </label>
                </div> 
              </div>
            </div>

            {/* Step 3 — Preview */}
            {(reportData || reportType === "individual") && (
              <div className="card" style={{ marginBottom:16 }}>
                <div className="card-header">
                  <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em" }}>3 · Preview</div>
                  {reportType === "individual"
                    ? (individualData && !individualData.noData) && <span style={{ fontSize:12, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>{individualData.total} case{individualData.total !== 1 ? "s" : ""} for {individualData.techName}</span>
                    : reportData?.total > 0 && <span style={{ fontSize:12, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>{reportData.total} case{reportData.total !== 1 ? "s" : ""} matched</span>}
                </div>

                {reportType === "individual" ? (
                  <IndividualPreview
                    data={individualData}
                    color={rawColor}
                    waveformLoading={waveformSummary === undefined}
                    onExportPDF={doExportPDF}
                    generating={generating}
                  />
                ) : reportData.total === 0 ? (
                  <div style={{ padding:"32px 24px", textAlign:"center" }}>
                    <div style={{ fontSize:14, fontWeight:600, color:"var(--text-secondary)", marginBottom:6 }}>No cases found</div>
                    <div style={{ fontSize:13, color:"var(--text-tertiary)" }}>No cases match the selected filters. Try widening the date range or changing filters.</div>
                  </div>
                ) : (
                  <div style={{ padding:"14px 18px" }}>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:16 }}>
                      <StatCard label="Overall Score"
                        value={reportData.overall == null ? "—" : `${reportData.overall}%`}
                        color={tierFor(reportData.overall).color}
                        sub={`${reportData.total} case${reportData.total !== 1 ? "s" : ""}`} />
                      <StatCard label="Documentation"
                        value={reportData.documentation.score == null ? "—" : `${reportData.documentation.score}%`}
                        color={tierFor(reportData.documentation.score).color}
                        sub={reportData.documentation.score == null
                          ? "No documentation checks scored"
                          : `${reportData.documentation.caseCount} case${reportData.documentation.caseCount !== 1 ? "s" : ""}`} />
                      <StatCard label="Waveform Analysis"
                        value={reportData.waveform.score == null ? "—" : `${reportData.waveform.score}%`}
                        color={tierFor(reportData.waveform.score).color} 
                        sub={reportData.waveform.score == null
                          ? "No waveform QA reviewed in this period"
                          : `${reportData.waveform.caseCount} case${reportData.waveform.caseCount !== 1 ? "s" : ""}`} />
                      <StatCard label="MSO Score" value="—" color="var(--text-tertiary)" sub="Not yet available" />
                    </div>

                    {/* Mini breakdown by procedure */}
                    {Object.keys(reportData.byProc).length > 0 && (
                      <div style={{ marginBottom:20 }}>
                        <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>By Procedure Type</div>
                        <div style={{ background:"var(--gray-50)", borderRadius:6, border:"1px solid var(--border)", overflow:"hidden" }}>
                          {Object.entries(reportData.byProc)
                            .sort((a,b)=>b[1].total-a[1].total).slice(0,6)
                            .sort((a,b)=>b[1].avg_score-a[1].avg_score)
                            .map(([pt,s], i, arr) => {
                            const t = tierFor(s.avg_score);
                            return (
                              <div key={pt} style={{ display:"flex", alignItems:"center", gap:16, padding:"8px 12px", borderBottom: i === arr.length - 1 ? "none" : "1px solid var(--border)" }}>
                                <span style={{ flex:1, fontSize:12, color:"var(--text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{pt}</span>
                                <span style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", flexShrink:0 }}>{s.total} case{s.total !== 1 ? "s" : ""}</span>
                                <span style={{ fontSize:10.5, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", flexShrink:0, width:64, textAlign:"right" }}
                                  title="Cases with waveform QA reviewed">{s.waveCases}/{s.total} wave</span>
                                <div style={{ width:80, height:4, background:"var(--gray-200)", borderRadius:2, flexShrink:0, overflow:"hidden" }}>
                                  <div style={{ width:`${Math.min(100, s.avg_score)}%`, height:"100%", borderRadius:2, background:t.color }}/>
                                </div>
                                <span style={{ fontSize:11, fontFamily:"var(--font-mono)", fontWeight:600, color:t.color, flexShrink:0, minWidth:40, textAlign:"right" }}>{s.avg_score}%</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Monthly Scores over Time if greater than Month */}
                    {reportData.techMatrix?.trend?.length > 1 && (() => {
                      const trend  = reportData.techMatrix.trend.filter(t => t.score != null);
                      if (trend.length < 2) return null;
                      const W = 620, H = 150, PAD_L = 44, PAD_R = 44, PAD_T = 12, PAD_B = 30;
                      const lo = Math.min(80, ...trend.map(t => t.score));
                      const hi = 100;
                      const x  = i => PAD_L + (i / (trend.length - 1)) * (W - PAD_L - PAD_R);
                      const y  = s => PAD_T + (1 - (s - lo) / (hi - lo)) * (H - PAD_T - PAD_B);
                      const step = (hi - lo) <= 12 ? 2 : 5;
                      const gridlines = [];
                      for (let v = Math.ceil(lo / step) * step; v <= hi; v += step) gridlines.push(v);
                      return (
                        <div style={{ marginBottom:20 }}>
                          <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Trend</div>
                          <div style={{ background:"var(--gray-50)", borderRadius:6, border:"1px solid var(--border)", padding:"10px 12px" }}>
                            <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto", display:"block" }}>
                              {gridlines.map(v => (
                                <g key={v}>
                                  <line x1={PAD_L} x2={W - PAD_R} y1={y(v)} y2={y(v)}
                                    stroke={TIERS.some(t => t.min === v) ? tierFor(v).color : "#94a3b8"}
                                    strokeOpacity={TIERS.some(t => t.min === v) ? 0.5 : 0.6} strokeDasharray="3 3" />
                                  <text x={PAD_L - 6} y={y(v) + 3} textAnchor="end" fontSize="9" fill="var(--text-tertiary)" fontFamily="var(--font-mono)">{v}</text>
                                </g>
                              ))}
                              <polyline fill="none" stroke="var(--blue-700, #1d4ed8)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"
                                points={trend.map((t, i) => `${x(i)},${y(t.score)}`).join(" ")} />
                              {trend.map((t, i) => (
                                <g key={t.month}>
                                  <circle cx={x(i)} cy={y(t.score)} r="3.5" fill={tierFor(t.score).color} />
                                  <text x={x(i)} y={y(t.score) - 9} textAnchor={i === 0 ? "start" : i === trend.length - 1 ? "end" : "middle"} fontSize="10" fontWeight="600" fill={tierFor(t.score).color} fontFamily="var(--font-mono)">{t.score}%</text>
                                  <text x={x(i)} y={H - 14} textAnchor={i === 0 ? "start" : i === trend.length - 1 ? "end" : "middle"} fontSize="10" fill="var(--text-tertiary)" fontFamily="var(--font-mono)">{t.label}</text>
                                  <text x={x(i)} y={H - 3} textAnchor={i === 0 ? "start" : i === trend.length - 1 ? "end" : "middle"} fontSize="8.5" fill="var(--text-tertiary)" fontFamily="var(--font-mono)">{t.caseCount} case{t.caseCount !== 1 ? "s" : ""}</text>
                                </g>
                              ))}
                            </svg>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Scores by Tech Matrix */}
                    {reportData.techMatrix?.rows?.length > 0 && (
                      <div style={{ marginBottom:20 }}>
                        <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>By Technologist</div>
                        <div style={{ background:"var(--gray-50)", borderRadius:6, border:"1px solid var(--border)", overflow:"hidden" }}>
                          {[...reportData.techMatrix.rows].sort((a, b) => b.score - a.score).map((r, i, arr) => {
                            const t = tierFor(r.score);
                            const floor = reportData.techMatrix.barFloor;
                            const width = Math.max(2, Math.round(((r.score - floor) / (100 - floor)) * 100));
                            return (
                              <div key={r.name} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", borderBottom: i === arr.length - 1 ? "none" : "1px solid var(--border)" }}>
                                <span style={{ fontSize:11.5, color:"var(--text-primary)", width:150, flexShrink:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.name}</span>
                                <span style={{ flex:1, minWidth:0 }}>
                                  <div style={{ height:8, background:"var(--gray-200)", borderRadius:4, overflow:"hidden" }}>
                                    <div style={{ width:`${width}%`, height:"100%", borderRadius:4, background:t.color }}/>
                                  </div>
                                </span>
                                <span style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", width:60, textAlign:"right", flexShrink:0 }}>{r.caseCount} case{r.caseCount !== 1 ? "s" : ""}</span>
                                <span style={{ fontSize:11.5, fontFamily:"var(--font-mono)", fontWeight:700, color:t.color, width:36, textAlign:"right", flexShrink:0 }}>{r.score}%</span>
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ display:"flex", gap:12, marginTop:8, flexWrap:"wrap", alignItems:"center" }}>
                          {TIERS.map(t => (
                            <span key={t.id} style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:10.5, color:"var(--text-tertiary)" }}>
                              <span style={{ width:9, height:9, borderRadius:2, background:t.color, display:"inline-block" }}/>
                              {t.label} {t.range}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Top failed checks preview — only for internal */}
                    {reportType === "internal" && reportData.topFailed.length > 0 && (
                      <div style={{ marginBottom:14 }}>
                        <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Top Failed Checks</div>
                        <div style={{ background:"var(--gray-50)", borderRadius:6, border:"1px solid var(--border)", overflow:"hidden" }}>
                          {reportData.topFailed.slice(0,5).map(c => (
                            <div key={c.check_id} style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 12px", borderBottom:"1px solid var(--border)" }}>
                              <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", flexShrink:0, minWidth:52 }}>{c.check_id}</span>
                              <span style={{ flex:1, fontSize:12, color:"var(--text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.question}</span>
                              <span style={{ fontSize:11, fontFamily:"var(--font-mono)", fontWeight:600, color:"var(--error)", flexShrink:0 }}>{c.fail_rate}% fail rate</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Documentation rate — show for medicare */}
                    {reportType === "medicare" && (
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:14 }}>
                        <StatCard label="IONM START Documented" value={`${reportData.docRates.start_rate}%`} color={reportData.docRates.start_rate>=90?"var(--success)":"var(--error)"} sub={`${reportData.docRates.start_count} of ${reportData.total}`} />
                        <StatCard label="IONM STOP Documented"  value={`${reportData.docRates.stop_rate}%`}  color={reportData.docRates.stop_rate>=90?"var(--success)":"var(--error)"}  sub={`${reportData.docRates.stop_count} of ${reportData.total}`} />
                        <StatCard label="Complete Timing"        value={`${reportData.docRates.both_rate}%`}  color={reportData.docRates.both_rate>=90?"var(--success)":"var(--error)"}  sub={`${reportData.docRates.both_count} billable`} />
                      </div>
                    )}

                    {reportData.unresolvedFailed.length > 0 && (
                      <div style={{ padding:"10px 12px", background:"rgba(239,68,68,0.06)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:6, fontSize:12, color:"var(--error)", marginBottom:14 }}>
                        <strong>⚠ {reportData.unresolvedFailed.length} unresolved failed case{reportData.unresolvedFailed.length>1?"s":""}</strong> — failed QA checks with no reviewer decision. These will be flagged in the exported report.
                      </div>
                    )}

                    {reportData.gaps.length > 0 && (
                      <div style={{ padding:"10px 12px", background:"#fffbeb", border:"1px solid #fde68a", borderRadius:6, fontSize:12, color:"#78350f", marginBottom:14 }}>
                        <div style={{ fontWeight:600, marginBottom:4 }}>⚠ Data gaps — will be noted in report:</div>
                        {reportData.gaps.map((g,i) => <div key={i} style={{ marginLeft:8 }}>· {g}</div>)}
                      </div>
                    )}

                    <div style={{ display:"flex", gap:10, paddingTop:4 }}>
                      <button className="btn btn-primary" onClick={doExportPDF} disabled={generating||!reportData.total} style={{ display:"flex", alignItems:"center", gap:7 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"/></svg>
                        Export PDF
                      </button>
                      <button className="btn btn-secondary" onClick={doExportCSV} disabled={generating||!reportData.total} style={{ display:"flex", alignItems:"center", gap:7 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 17v-2m3 2v-4m3 4v-6M4 4h16a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1z"/></svg>
                        Export Excel (CSV)
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <MonthlyEmailPanel />
      </div>
    </>
  );
}